/**
 * Type analysis, resolving expressions, etc.
 */

import {
	ArrayAccessTree,
	ArrayCreationTree,
	ArrayTypeTree,
	BaseExpressionTree,
	BaseTree,
	BinaryOperator,
	BinaryOperatorTree,
	BlockTree,
	BooleanLiteralTree,
	BreakStatementTree,
	CharacterLiteralTree,
	ClassCreationTree,
	ClassDeclarationTree,
	ClassLiteralTree,
	ContinueStatementTree,
	DoWhileLoopTree,
	EmptyStatementTree,
	EnhancedForLoopTree,
	EnumDeclarationTree,
	ExpressionStatementTree,
	FieldAccessTree,
	FloatLiteralTree,
	ForLoopTree,
	IdentTree,
	IfStatementTree,
	IntegerLiteralTree,
	InterfaceDeclarationTree,
	LabelledStatementTree,
	LambdaExpressionTree,
	LocalVariableDeclarationListTree,
	LocalVariableDeclarationTree,
	MethodInvocationTree,
	MethodReferenceTree,
	modifiersToAccessorFlags,
	NameTree,
	NullLiteralTree,
	ParenthesizedExpressionTree,
	PrimitiveTypeTree,
	ReferenceTypeTree,
	StringLiteralTree,
	SuperTree,
	SwitchStatementTree,
	TernaryOperatorTree,
	ThisTree,
	ThrowStatementTree,
	ThrowsTree,
	TryStatementTree,
	TypeArgumentsTree,
	TypeCastTree,
	TypeParametersTree,
	TypeParameterTree,
	UnaryOperatorTree,
	WhileLoopTree,
	WildcardTree,
} from "./model";
import {Keyword, Operator, tokenTypeToString} from "./lexer";
import {
	AccessorFlags,
	AnyType,
	ArrayType,
	ClassDeclarationInfo,
	DeclarationInfoBase,
	GeneralType,
	GeneralTypeOrAny,
	hasFlag,
	InterfaceDeclarationInfo,
	InterfaceOrClassMixin,
	LabelInfo,
	LocalVariableDef,
	maybeUnparameterize,
	MemberFieldInfo,
	mergeAmbiguous,
	MethodDeclarationInfo,
	NonGenericType,
	NullType,
	PackageInfo,
	ParameterizedType,
	PermittedSuperclassType,
	PossiblyAmbiguous,
	prettyPrintInfo,
	prettyPrintType,
	PrimitiveType,
	RawType,
	ReferenceType,
	TypeDeclarationInfo,
	TypeParameterInfo,
	TypeVariableType,
	unparameterize,
	WildcardType,
} from "./type_model";
import {
	cafebabeAssert,
	cafebabeUnreachable,
	CompileError,
	JavaVersion,
	Span,
} from "./error";
import {isValidJavaIdentifier} from "./character_table";

/**
 * The scope chain generally looks as follows:
 *
 * root -> package -> cu (compilation unit) -> top-level class (-> nested class) -> function -> block ...
 */
export type ScopeChainNode = {
	scopeType: "block" | "function" | "class" | "cu" | "root" | "package";

	/**
	 * List of accessible types defined in this scope via fully-qualified, explicit imports, or by virtue of being
	 * in the same package as the compilation unit.
	 */
	types?: Map<string, ClassDeclarationInfo | InterfaceDeclarationInfo>;

	/**
	 * List of static variable imports defined in this scope. Conflicts are guaranteed to be prevented during the
	 * import phase (unlike for import-on-demands).
	 *
	 * e.g. import static java.lang.Math.PI;
	 */
	staticallyImportedVariables?: Map<string, MemberFieldInfo>;

	/**
	 * List of static method imports defined in this scope. Each method may have multiple overloads to choose from;
	 * it is possible to statically import multiple methods,
	 */
	staticallyImportedMethods?: Map<string, MethodDeclarationInfo[]>;

	/**
	 * e.g. import java.util.*;      -> java.util.ArrayList now among the importsOnDemand
	 * e.g. import java.util.Map.*;  -> java.util.Map.Entry now among the importsOnDemand
	 */
	importOnDemandTypes?: Map<
		string,
		PossiblyAmbiguous<ClassDeclarationInfo | InterfaceDeclarationInfo>
	>;

	/**
	 * e.g. import static java.lang.Math.*;  -> java.lang.Math.PI now among the importOnDemandVariables.
	 */
	importOnDemandVariables?: Map<
		string,
		PossiblyAmbiguous<ClassDeclarationInfo | InterfaceDeclarationInfo>
	>;

	/**
	 * List of accessible member functions defined in this scope via import-on-demand declarations.
	 *
	 * e.g. import static java.lang.Short.*;  -> parseShort now among the importOnDemandMethods.
	 */
	importOnDemandMethods?: Map<string, MethodDeclarationInfo[]>;

	/**
	 * List of type parameters defined in this scope.
	 */
	typeParameters?: Map<string, TypeParameterInfo>;

	/**
	 * List of accessible local variables defined in this scope.
	 */
	locals?: Map<string, LocalVariableDef>;

	/**
	 * List of class functions defined in this scope by virtue of being in the same class, or a subclass of another
	 * class.
	 */
	memberDeclarations?: Map<string, MethodDeclarationInfo[]>;

	/**
	 * List of class fields defined in this scope by virtue of being in the same class, or a subclass of another
	 * class.
	 */
	memberFields?: Map<string, MemberFieldInfo>;

	/**
	 * List of root-level packages defined in this scope.
	 */
	packages?: Map<string, PackageInfo>;

	/**
	 * List of labels defined in this scope.
	 */
	labels?: Map<string, LabelInfo>;

	/**
	 * Previous node in the scope chain.
	 */
	prev: ScopeChainNode | null;

	/**
	 * Associated info for the scope
	 */
	info?:
		| PackageInfo
		| MethodDeclarationInfo
		| ClassDeclarationInfo
		| InterfaceDeclarationInfo
		| null;

	/**
	 * Optional, associated tree for the scope.
	 */
	tree?: BaseTree | null;

	/**
	 * Whether the scope is considered "static", i.e., variable references must resolve statically.
	 */
	isStatic?: boolean;
};

type WalkOptions = {
	excludeBuiltin?: boolean;
	excludeNested?: boolean;
};

/**
 * Passed around while analyzing. (Scope is usually passed separately.)
 */
export type AnalysisCtx = {
	reportError: (error: CompileError) => void;
	javaVersion: JavaVersion;
};

/**
 * Returns true if the package is now empty after the callback has executed.
 */
export function walkPackages(
	root: PackageInfo,
	options: WalkOptions,
	callback: (info: PackageInfo) => boolean
) {
	if (!callback(root)) return false;
	const queue: PackageInfo[] = [...root.subpackages.values()];
	while (queue.length) {
		const pkg = queue.shift()!;
		if (pkg.builtin && options.excludeBuiltin) continue;

		if (!callback(pkg)) return false;
		queue.push(...pkg.subpackages.values());
	}
	return true;
}

/**
 * Walk the scope chain and return the first non-null result from the callback, or null if nothing
 * succeeded.
 * @param scope The scope to walk through.
 * @param callback The callback, which takes in the scope, and whether we passed through a static context on the way
 * to the given scope. The first callback which returns a non-null result will be the result of walkScopeChain.
 */
export function walkScopeChain<T>(
	scope: ScopeChainNode | null,
	callback: (node: ScopeChainNode, static_: boolean) => T | null | undefined
) {
	let static_ = scope?.isStatic ?? false;
	while (scope) {
		const result = callback(scope, static_);
		if (result != null) return result;
		scope = scope.prev;
		static_ ||= !!scope?.isStatic;
	}
	return null;
}

export function tryInsertType<V extends {simpleName: string}>(
	map: Map<string, V>,
	decl: V,
	span: Span
): CompileError | undefined {
	const name = decl.simpleName;
	if (map.has(name)) {
		return new CompileError(`Duplicate class/interface ${name}`, span);
	}
	map.set(name, decl);
}

/**
 * Check whether the given declaration is accessible from the given package and file. Returns an error string if
 * the declaration is inaccessible, and nothing otherwise.
 */
export function accessibilityError(
	decl:
		| ClassDeclarationInfo
		| InterfaceDeclarationInfo
		| MethodDeclarationInfo
		| MemberFieldInfo,
	scope: ScopeChainNode,
	isStatic = false
): string | undefined {
	const scopePackage = packageOfScope(scope);

	if (decl.type === "function" || decl.type === "member") {
		const memberOf = decl.memberOf;
		if (memberOf === "array") return; // .length field of arrays is always accessible

		const staticallyAccessible =
			!isStatic || hasFlag(decl, AccessorFlags.static);
		if (!staticallyAccessible)
			return "non-static member accessed from static context";

		// If the member or function is declared in a scope containing this scope, it is accessible
		const directlyInScope = walkScopeChain(scope, (node, static_) => {
			if (
				decl.type === "member" &&
				node?.memberFields?.get(decl.name) === decl
			) {
				return true;
			}
			if (
				decl.type === "function" &&
				node?.memberDeclarations?.get(decl.name)?.includes(decl)
			) {
				return true;
			}
		});
		if (directlyInScope) return;

		const memberIsPossiblyAccessible =
			hasFlag(decl, AccessorFlags.public) ||
			(memberOf.package === scopePackage &&
				!hasFlag(decl, AccessorFlags.private));
		if (!memberIsPossiblyAccessible) return "private member or function";
		return accessibilityError(memberOf, scope);
	}

	if (decl.package === scopePackage) {
		// If we're in the same package, then if the (inner) class is declared private, it still cannot be used unless
		// the class is directly in scope
		if (
			hasFlag(decl, AccessorFlags.private) &&
			lookupType(scope, decl.simpleName) !== decl
		) {
			return "class is private";
		}
	} else {
		// Only "public" classes can be used from outside a package
		if (!hasFlag(decl, AccessorFlags.public)) {
			return "class is not public";
		}
	}
}

/** Cached primitive references */
const primitiveRefs: Record<
	PrimitiveType["primitive"],
	Readonly<PrimitiveType>
> = {
	void: {type: "primitive", primitive: "void", boxed: "java.lang.Void"},
	byte: {type: "primitive", primitive: "byte", boxed: "java.lang.Byte"},
	char: {type: "primitive", primitive: "char", boxed: "java.lang.Character"},
	short: {type: "primitive", primitive: "short", boxed: "java.lang.Short"},
	int: {type: "primitive", primitive: "int", boxed: "java.lang.Integer"},
	float: {type: "primitive", primitive: "float", boxed: "java.lang.Float"},
	long: {type: "primitive", primitive: "long", boxed: "java.lang.Long"},
	double: {type: "primitive", primitive: "double", boxed: "java.lang.Double"},
	boolean: {
		type: "primitive",
		primitive: "boolean",
		boxed: "java.lang.Boolean",
	},
};

/** Map between boxed class and the corresponding PrimitiveType */
const primitiveUnboxingMap: {[binaryName: string]: Readonly<PrimitiveType>} = {
	"java.lang.Void": primitiveRefs["void"],
	"java.lang.Byte": primitiveRefs["byte"],
	"java.lang.Character": primitiveRefs["char"],
	"java.lang.Short": primitiveRefs["short"],
	"java.lang.Integer": primitiveRefs["int"],
	"java.lang.Float": primitiveRefs["float"],
	"java.lang.Long": primitiveRefs["long"],
	"java.lang.Double": primitiveRefs["double"],
	"java.lang.Boolean": primitiveRefs["boolean"],
};

/**
 * Attempt to primitive-unbox the given type, and return the unboxed result if successful.
 * @param ty The type to unbox.
 * @param disallowVoid Whether to disallow the unboxing conversion of java.lang.Void to void.
 */
export function tryUnbox(
	ty: GeneralTypeOrAny,
	disallowVoid = false
): Readonly<PrimitiveType> | null {
	if (ty.type === "primitive") return ty;
	if (ty.type !== "nongeneric") return null; // java.lang.Double etc. are final, so nothing to check here
	const result = primitiveUnboxingMap[ty.ty.binaryName];
	return !result || (disallowVoid && result.primitive === "void")
		? null
		: result;
}

/**
 * Attempt to box the type, if it is a primitive, and otherwise pass it through.
 */
function maybeBox(
	scope: ScopeChainNode,
	ty: GeneralTypeOrAny
): GeneralTypeOrAny {
	if (ty.type === "primitive") {
		const java_lang_Boxed = lookupBinaryName(scope, ty.boxed);
		cafebabeAssert(java_lang_Boxed !== null, "Boxed primitive must exist");
		return instantiateNonGenericType(java_lang_Boxed);
	}

	return ty;
}

/**
 * Attempt to promote a unary numeric operand. Returns null on failure to promote.
 * See https://docs.oracle.com/javase/specs/jls/se8/html/jls-5.html#jls-5.6.1
 */
export function unaryNumericPromote(
	ty: Readonly<GeneralTypeOrAny> | null
): PrimitiveType | null {
	if (ty === null) return null;
	switch (ty.type) {
		case "nongeneric":
		case "primitive":
			let unboxed = tryUnbox(ty, true);
			const primitive = unboxed?.primitive;
			if (!primitive || primitive === "boolean") return null;
			// "Otherwise, if the operand is of compile-time type byte, short, or char, it is promoted to a value of
			// type int by a widening primitive conversion"
			if (
				primitive === "byte" ||
				primitive === "short" ||
				primitive === "char"
			) {
				unboxed = primitiveRefs.int;
			}
			return unboxed;
	}
	return null;
}

/**
 * Attempt to promote binary numeric operands. Returns null on failure to promote.
 * See https://docs.oracle.com/javase/specs/jls/se8/html/jls-5.html#jls-5.6.2
 */
export function binaryNumericPromote(
	ty1: GeneralTypeOrAny,
	ty2: GeneralTypeOrAny
): GeneralTypeOrAny | null {
	// propagate any
	if (ty1.type === "any") return ty1;
	if (ty2.type === "any") return ty2;

	const unboxed1 = tryUnbox(ty1, true);
	const unboxed2 = tryUnbox(ty2, true);

	// "... each of which must denote a value that is convertible to a numeric type ..."
	if (
		!unboxed1 ||
		!unboxed2 ||
		unboxed1 === primitiveRefs.boolean ||
		unboxed2 === primitiveRefs.boolean
	)
		return null;

	// Widening primitive conversion (§5.1.2) is applied to convert either or both operands as specified by the following rules:
	// 1. If either operand is of type double, the other is converted to double.
	// 2. Otherwise, if either operand is of type float, the other is converted to float.
	// 3. Otherwise, if either operand is of type long, the other is converted to long.
	// 4. Otherwise, both operands are converted to type int.
	for (const prim of [
		primitiveRefs.double,
		primitiveRefs.float,
		primitiveRefs.long,
	]) {
		if (unboxed1 === prim || unboxed2 === prim) return prim;
	}

	return primitiveRefs.int;
}

function _resolveUnaryExpressionType(
	scope: ScopeChainNode,
	tree: UnaryOperatorTree,
	ctx: AnalysisCtx
): GeneralTypeOrAny {
	if (!tree.operand) return anyType();

	const ty = resolveExpressionType(scope, tree.operand, ctx);
	if (ty.type === "any") return ty;

	const unboxed = tryUnbox(ty, true);
	if (unboxed) {
		// https://docs.oracle.com/javase/specs/jls/se8/html/jls-15.html#jls-15.15.1
		switch (tree.operatorTag) {
			// Unary numeric promotion (§5.6.1) is performed on the operand. The type of the unary plus expression is
			// the promoted type of the operand.
			case Operator.Plus:
			case Operator.Minus:
			case Operator.Tilde:
				if (unboxed === primitiveRefs.boolean) break;

				const result = unaryNumericPromote(unboxed);
				cafebabeAssert(
					result !== null,
					"_resolveUnaryExpressionType: conv should succeed"
				);
				return result;
			case Operator.Not:
				// The type of the operand expression of the unary ! operator must be boolean or Boolean, or a
				// compile-time error occurs.
				if (unboxed !== primitiveRefs.boolean) break;
				return unboxed;
			case Operator.PlusPlus:
			case Operator.MinusMinus:
				// The result of the unary expression must be a variable of a type that is convertible (§5.1.8) to a
				// numeric type, or a compile-time error occurs.
				if (unboxed === primitiveRefs.boolean) break;
				return unboxed;
		}
	}

	ctx.reportError(
		new CompileError(
			`Operator '${tokenTypeToString(tree.operatorTag)}' cannot be applied to type ${prettyPrintType(ty)}`,
			tree.span
		)
	);

	return anyType();
}

/**
 * Check whether the type is "reifiable" -- see https://docs.oracle.com/javase/specs/jls/se8/html/jls-4.html#jls-4.7.
 * Reifiable types are those types whose information is completely known at runtime, e.g., generics are erased.
 *
 * Returns an error string if the type is not reifiable. Error string is like the clause "is a type parameter".
 */
export function checkTypeReifiable(ty: GeneralTypeOrAny): string | undefined {
	// A type is reifiable if...

	switch (ty.type) {
		case "array":
			// ... It is an array type (§10.1) whose element type is reifiable.
			return checkTypeReifiable(ty.arrayOf);
		case "any":
		case "null":
		case "primitive":
		// ... It is a primitive type (§4.2).
		case "raw":
		case "nongeneric":
			// ... It refers to a non-generic class or interface type declaration.
			// ... It is a raw type.
			return undefined;
		case "intersection":
			return "is an intersection type";
		case "typevar":
			return "contains a type parameter";
	}

	// ... It is a parameterized type in which all type arguments are unbounded wildcards
	if (
		ty.typeArguments.some(
			(arg) => arg.type !== "wildcard" || arg.boundKind !== "none"
		)
	)
		return "contains a parameterized type";
}

export function isClassOfBinaryName(ty: GeneralTypeOrAny, binaryName: string) {
	return maybeUnparameterize(ty)?.binaryName === binaryName;
}

function isVoid(
	ty: GeneralTypeOrAny
): ty is PrimitiveType & {primitive: "void"} {
	return ty === primitiveRefs.void;
}

function _resolveBinaryExpressionType(
	scope: ScopeChainNode,
	tree: BinaryOperatorTree,
	ctx: AnalysisCtx
): GeneralTypeOrAny {
	const leftTy = resolveExpressionType(scope, tree.left, ctx);
	const rightTy = resolveExpressionType(scope, tree.right, ctx);

	if (leftTy.type === "any" || rightTy.type === "any") return anyType(); // propagate any

	const op = tree.operator.type as BinaryOperator;

	// https://docs.oracle.com/javase/specs/jls/se8/html/jls-15.html#jls-15.17
	if (!isVoid(leftTy) && !isVoid(leftTy)) {
		switch (op) {
			case Operator.Percent:
			case Operator.PercentEq:
			case Operator.Star:
			case Operator.StarEq:
			case Operator.Slash:
			case Operator.SlashEq: {
				// https://docs.oracle.com/javase/specs/jls/se8/html/jls-15.html#jls-15.17 "Multiplicative operators"
				const promoted = binaryNumericPromote(leftTy, rightTy);
				if (!promoted) break;
				return promoted;
			}
			case Operator.LtLt:
			case Operator.GtGt:
			case Operator.GtGtGt:
			case Operator.LtLtEq:
			case Operator.GtGtEq:
			case Operator.GtGtGtEq: {
				// https://docs.oracle.com/javase/specs/jls/se8/html/jls-15.html#jls-15.19 "Shift Operators"
				const promotedLeft = unaryNumericPromote(leftTy),
					promotedRight = unaryNumericPromote(rightTy);
				if (!promotedLeft || !promotedRight) break;

				// "The type of the shift expression is the promoted type of the left-hand operand."
				return promotedLeft;
			}
			case Operator.Lt:
			case Operator.Gt:
			case Operator.LtEq:
			case Operator.GtEq: {
				// https://docs.oracle.com/javase/specs/jls/se8/html/jls-15.html#jls-15.20.1 "Numerical Comparison Operators"
				const promoted = binaryNumericPromote(leftTy, rightTy);
				if (!promoted) break;
				return primitiveRefs.boolean;
			}
			case Operator.EqEq:
			case Operator.NotEq: {
				// https://docs.oracle.com/javase/specs/jls/se8/html/jls-15.html#jls-15.21 "Equality Operators"
				// The equality operators may be used to compare ...

				// ... two operands that are each of either reference type or the null type
				if (leftTy.type !== "primitive" && rightTy.type !== "primitive") {
					return primitiveRefs.boolean;
				}

				const lhsUnboxed = tryUnbox(leftTy, true);
				const rhsUnboxed = tryUnbox(rightTy, true);

				// ... two operands of type boolean or Boolean
				// ... two operands that are convertible (§5.1.8) to numeric type
				if (
					(lhsUnboxed === primitiveRefs.boolean &&
						rhsUnboxed === primitiveRefs.boolean) ||
					(unaryNumericPromote(lhsUnboxed) && unaryNumericPromote(rhsUnboxed))
				) {
					return primitiveRefs.boolean;
				}

				break;
			}
			case Operator.And:
			case Operator.Or:
			case Operator.Caret:
			case Operator.AndEq:
			case Operator.OrEq:
			case Operator.CaretEq: {
				// https://docs.oracle.com/javase/specs/jls/se8/html/jls-15.html#jls-15.22 "Bitwise and Logical Operators"
				const lhsUnboxed = tryUnbox(leftTy, true);
				const rhsUnboxed = tryUnbox(rightTy, true);

				if (!lhsUnboxed || !rhsUnboxed) break;
				if (
					lhsUnboxed === primitiveRefs.boolean &&
					rhsUnboxed === primitiveRefs.boolean
				)
					return primitiveRefs.boolean;

				const promoted = binaryNumericPromote(lhsUnboxed, rhsUnboxed);
				if (promoted) return promoted;

				break;
			}
			case Operator.PlusEq:
			case Operator.Plus: {
				// https://docs.oracle.com/javase/specs/jls/se8/html/jls-15.html#jls-15.18.1 "String Concatenation Operator +"

				const leftIsString = isClassOfBinaryName(leftTy, "java.lang.String");
				const rightIsString = isClassOfBinaryName(rightTy, "java.lang.String");

				if ((op !== Operator.PlusEq && leftIsString) || rightIsString) {
					return leftIsString ? leftTy : rightTy; // String can be added to anything
				}
			} // fallthrough
			case Operator.MinusEq:
			case Operator.Minus: {
				const promoted = binaryNumericPromote(leftTy, rightTy);
				if (promoted) return promoted;
				break;
			}
			case Operator.AndAnd:
			case Operator.OrOr: {
				// https://docs.oracle.com/javase/specs/jls/se8/html/jls-15.html#jls-15.23
				const lhsUnboxed = tryUnbox(leftTy, true);
				const rhsUnboxed = tryUnbox(rightTy, true);

				if (
					lhsUnboxed === primitiveRefs.boolean &&
					rhsUnboxed === primitiveRefs.boolean
				)
					return primitiveRefs.boolean;

				break;
			}
			case Keyword.instanceof: {
				cafebabeAssert(
					rightTy.type !== "primitive",
					"instanceof parse should not have a primitive type"
				);
				const reifiableError = checkTypeReifiable(rightTy);
				if (reifiableError) {
					ctx.reportError(new CompileError(reifiableError, tree.right!.span));
				}
				// TODO: Check cast
				if (leftTy.type === "primitive") {
					ctx.reportError(
						new CompileError(
							"Left-hand side of instanceof operator must be a reference type",
							tree.left!.span
						)
					);
				}

				return primitiveRefs.boolean;
			}
		}
	}

	ctx.reportError(
		new CompileError(
			`Operator '${tokenTypeToString(op)}' cannot be applied to types <code>${prettyPrintType(leftTy)}</code> and <code>${prettyPrintType(rightTy)}</code>`,
			tree.span
		)
	);
	return anyType();
}

/**
 * Resolve the type of an array type tree, e.g. "int[][]".
 */
function resolveArrayTypeTree(
	scope: ScopeChainNode,
	tree: ArrayTypeTree,
	ctx: AnalysisCtx
): ArrayType | AnyType {
	const arrayDims = tree.bracketsTree.bracketPairs.length;
	const elem = resolveTypeTree(scope, tree.type, ctx);

	// propagate any and resolution failures
	if (!elem) return anyType();
	if (elem.type === "any") return elem;

	cafebabeAssert(
		elem.type !== "wildcard" && elem.type !== "null",
		"Array of wildcard or nulls is impossible"
	);
	let finalTy = elem;
	for (let i = 0; i < arrayDims; ++i)
		finalTy = {type: "array", arrayOf: finalTy};
	return finalTy as ArrayType;
}

function packageOfScope(scope: ScopeChainNode): PackageInfo | null {
	while (scope.scopeType !== "package") {
		if (!scope.prev) return null; // reached root
		scope = scope.prev;
	}
	const result = scope.info as PackageInfo;
	cafebabeAssert(
		result.type === "package",
		"Info of package scope should be set"
	);
	return result;
}

function substituteTypeParameters<T extends GeneralTypeOrAny | WildcardType>(
	ty: T,
	map: Map<TypeParameterInfo, ReferenceType | WildcardType> | null
): TypeVariableType extends T ? GeneralTypeOrAny | WildcardType : T {
	if (!map || !ty) return ty;
	switch (ty.type) {
		case "intersection":
			return {
				type: "intersection",
				components: ty.components.map((c) => substituteTypeParameters(c, map)),
			} as T;
		case "typevar":
			if (ty.typeParameter) {
				const new_ = map.get(ty.typeParameter);
				return (new_ as T) ?? ty;
			} else {
				return {
					type: "typevar",
					typeParameter: null,
					upperBound: ty.upperBound
						? substituteTypeParameters(ty.upperBound, map)
						: null,
					lowerBound: ty.lowerBound
						? substituteTypeParameters(ty.lowerBound, map)
						: null,
				} as T;
			}
		case "any":
		case "primitive":
		case "null":
		case "raw":
		case "nongeneric":
			return ty;
		case "parameterized":
			return {
				type: "parameterized",
				typeArguments: ty.typeArguments.map((c) =>
					substituteTypeParameters(c, map)
				),
				parameterizes: ty.parameterizes,
			} as T;
		case "array":
			return {
				type: "array",
				arrayOf: substituteTypeParameters(ty.arrayOf, map),
			} as ArrayType as T;
		case "wildcard":
			return {
				type: "wildcard",
				boundKind: ty.boundKind,
				bound: ty.bound ? substituteTypeParameters(ty.bound, map) : null,
			} as WildcardType as T;
	}
}

function inferTPMap(
	ty: ParameterizedType
): Map<TypeParameterInfo, ReferenceType | WildcardType> {
	const map: Map<TypeParameterInfo, ReferenceType | WildcardType> = new Map();
	const underlying = unparameterize(ty);
	for (let i = 0; i < ty.typeArguments.length; ++i) {
		map.set(underlying.typeParameters[i]!, ty.typeArguments[i]!);
	}
	return map;
}

const EMPTY_MAP = new Map();
function maybeInferTPMap(
	ty: GeneralTypeOrAny
): Map<TypeParameterInfo, ReferenceType | WildcardType> {
	return ty.type === "parameterized" ? inferTPMap(ty) : EMPTY_MAP;
}

export function instantiateType(
	member: ClassDeclarationInfo | InterfaceDeclarationInfo,
	tyArgs: Map<TypeParameterInfo, ReferenceType | WildcardType> = new Map()
): ParameterizedType | NonGenericType {
	if (member.typeParameters.length === 0) {
		return {
			type: "nongeneric",
			ty: member,
		};
	}
	const args: (ReferenceType | WildcardType)[] = member.typeParameters.map(
		(info) =>
			tyArgs.get(info) ?? {
				type: "typevar",
				typeParameter: info,
				upperBound: info.extends,
				lowerBound: null,
			}
	);

	return {
		type: "parameterized",
		typeArguments: args,
		parameterizes: member,
	};
}

export function instantiateNonGenericType(
	member: ClassDeclarationInfo | InterfaceDeclarationInfo
): NonGenericType | RawType {
	return member.typeParameters.length === 0
		? {
				type: "nongeneric",
				ty: member,
			}
		: {
				type: "raw",
				rawOf: member,
			};
}

export function isReferenceType(ty: GeneralTypeOrAny): ty is ReferenceType {
	return !(ty.type === "any" || ty.type === "null" || ty.type === "primitive");
}

/**
 * Given the type of an expression, look up the member field of the type. Accessibility checks are not performed.
 */
export function lookupMember(
	scope: ScopeChainNode,
	ty: GeneralTypeOrAny,
	name: string
): PossiblyAmbiguous<MemberFieldInfo> | null {
	if (!isReferenceType(ty)) return null;
	if (ty.type === "array") {
		return name === "length" ? arrayLengthField : null; // .length is only field on all arrays
	}

	let candidates: PossiblyAmbiguous<MemberFieldInfo> | null = null;
	walkInheritanceHierarchy(
		scope,
		ty,
		(clazz) => {
			const candidate = maybeUnparameterize(clazz)?.members.get(name) ?? null;
			candidates = mergeAmbiguous(candidates, candidate);
			return candidate !== null; // don't explore superclasses as the variable has been shadowed
		},
		true
	);
	return candidates;
}

export enum StaticPolicy {
	ALL,
	STATIC_ONLY,
	NONSTATIC_ONLY,
}

export function listMembers(
	scope: ScopeChainNode,
	staticPolicy: StaticPolicy,
	ty: GeneralTypeOrAny
): Map<string, MemberFieldInfo> {
	if (!isReferenceType(ty)) return new Map();
	if (ty.type === "array") {
		return new Map([["length", arrayLengthField]]);
	}

	const candidates: Map<string, PossiblyAmbiguous<MemberFieldInfo>> = new Map();

	/**
	 * Visit the members of the type.
	 * @param clazz The type.
	 * @param shadowed The list of field names which are currently shadowed, and thus should not be considered.
	 */
	function visit(clazz: ReferenceType, shadowed: Set<string>) {
		const members = maybeUnparameterize(clazz)?.members;
		const shadowing: string[] = [];

		if (members) {
			for (const [memberName, member] of members) {
				if (shadowed.has(memberName)) continue;
				const existing = candidates.get(memberName);
				if (!existing) {
					candidates.set(memberName, member);
				} else {
					candidates.set(memberName, mergeAmbiguous(existing, member)!);
				}
				shadowing.push(memberName);
				shadowed.add(memberName);
			}
		}

		for (const super_ of instantiateSuperclasses(scope, clazz, true)!) {
			visit(super_, shadowed);
		}

		shadowing.forEach((s) => shadowed.delete(s));
	}

	// Collect members in scope ignoring the inheritance hierarchy (may duplicate ones we visit later -- no matter)
	walkScopeChain(scope, (scopeNode, static_) => {
		const fields = scopeNode.memberFields;
		if (!fields) return;

		for (const [fieldName, info] of fields) {
			if (!hasFlag(info, AccessorFlags.static) && static_) continue;
			candidates.set(fieldName, info);
		}
	});

	// Collect all members
	visit(ty, new Set());
	// Remove members which are not accessible due to access rules, being ambiguous, or having the wrong static flag
	for (const [memberName, resolvedMember] of candidates) {
		const delete_ =
			resolvedMember.type === "ambiguous" ||
			accessibilityError(resolvedMember, scope, false) ||
			!(
				staticPolicy === StaticPolicy.ALL ||
				(staticPolicy === StaticPolicy.STATIC_ONLY) ===
					hasFlag(resolvedMember, AccessorFlags.static)
			);

		if (delete_) {
			candidates.delete(memberName); // JS guarantees iterator stability so this is ok
		}
	}

	return candidates as Map<string, MemberFieldInfo>; // all ambiguities have been removed in the for loop above
}

/**
 * Given a (possibly parameterized) reference type, look up the member class associated with the given identifier.
 *
 * For example, if the left hand side is the reference Map<K, V>, then "ident" might be the class "Entry".
 */
function lookupMemberClass(
	lhsResolved: ReferenceType,
	ident: string,
	permitPrivate: boolean
): PossiblyAmbiguous<NonGenericType | ParameterizedType | RawType> | null {
	switch (lhsResolved.type) {
		case "intersection": {
			cafebabeAssert(
				lhsResolved.components.length >= 2,
				"lookupMemberClass: intersection type requirement"
			);
			const candidates = lhsResolved.components.map((c) =>
				lookupMemberClass(c, ident, false)
			);

			let candidate = mergeAmbiguous(candidates[0]!, candidates[1]!);
			for (let i = 2; i < candidates.length; ++i)
				candidate = mergeAmbiguous(candidate, candidates[i]!);
			return candidate;
		}
		case "raw":
		case "nongeneric":
		case "parameterized": {
			const underlying = unparameterize(lhsResolved);
			const inferred = maybeInferTPMap(lhsResolved);
			const member = underlying.memberClasses.get(ident);

			if (!member) {
				// Explore extends and implements clauses
				let candidates: ReturnType<typeof lookupMemberClass> = null;
				if (underlying.extends) {
					const iter = Array.isArray(underlying.extends)
						? underlying.extends
						: [underlying.extends];
					for (const superinterfaceOrClass of iter) {
						const ext = substituteTypeParameters(
							superinterfaceOrClass,
							inferred
						);
						const result = lookupMemberClass(ext, ident, false);
						candidates = mergeAmbiguous(candidates, result);
					}
				} else if (underlying.type === "class" && underlying.implements) {
					for (const r of underlying.implements) {
						candidates = mergeAmbiguous(
							candidates,
							lookupMemberClass(
								substituteTypeParameters(r, inferred),
								ident,
								false
							)
						);
					}
				}

				return candidates;
			}

			if (hasFlag(member, AccessorFlags.private) && !permitPrivate) return null;
			return lhsResolved.type === "raw" &&
				!hasFlag(member, AccessorFlags.static)
				? instantiateNonGenericType(member)
				: instantiateType(member, inferred);
		}
		case "typevar": {
			return lhsResolved.upperBound
				? lookupMemberClass(lhsResolved.upperBound, ident, false)
				: null;
		}
		case "array":
			return null;
	}
	cafebabeUnreachable("lookupMemberClass");
}

function addTypeArgs(
	scope: ScopeChainNode,
	ty: NonGenericType | ParameterizedType | RawType,
	tree: TypeArgumentsTree,
	ctx: AnalysisCtx
): NonGenericType | ParameterizedType | RawType | null {
	const args = tree.args.map(
		(arg) => resolveTypeTree(scope, arg, ctx) as ReferenceType | WildcardType
	);
	if (args.some((arg) => arg === null)) {
		// failed to resolve at least one type argument :(
		return null;
	}
	if (ty.type === "raw") {
		ty = instantiateType(ty.rawOf, new Map());
	}
	if (ty.type !== "parameterized") {
		// Trying to add type arguments to a non-parameterized type
		ctx.reportError(
			new CompileError(`Type arguments not allowed here`, tree.span)
		);
		return null;
	}
	const underlying = ty.parameterizes;
	const params = underlying.typeParameters.filter(
		(p) => p.parameterOf === underlying
	);
	if (args.length !== params.length) {
		if (args.length === 0) {
			// Diamond type
			return ty;
		}

		ctx.reportError(
			new CompileError(
				`Incorrect number of type parameters (expected ${params.length}, got ${args.length})`,
				tree.span
			)
		);
		return null;
	}
	const map = new Map<TypeParameterInfo, ReferenceType | WildcardType>();
	for (let i = 0; i < args.length; ++i) {
		map.set(params[i]!, args[i]!);
	}
	return substituteTypeParameters(ty, map);
}

function _resolveTopLevelReferenceTypeTree(
	scope: ScopeChainNode,
	tree: ReferenceTypeTree | IdentTree,
	ctx: AnalysisCtx
): PackageInfo | ReferenceType | AnyType {
	const ident = tree instanceof ReferenceTypeTree ? tree.identifier : tree;
	const rhs = ident.identifier;

	// top level thing: look for type in scope, then package in scope
	const ty = lookupType(scope, rhs);
	if (!ty) {
		tree.referent = null;
		const result = (ident.referent =
			getRootScope(scope).packages!.get(rhs) ?? null);
		if (result === null) {
			ctx.reportError(new CompileError(`Cannot find type ${rhs}`, ident.span));
		}
		return (tree.referent = ident.referent = result) ?? anyType();
	}
	if (ty.type === "ambiguous") {
		ctx.reportError(
			new CompileError("Reference to type is ambiguous", ident.span)
		);
		return (ident.referent = ident.referent = null) ?? anyType();
	}
	tree.referent = ident.referent = ty;
	if (ty.type === "tparam") {
		return {
			type: "typevar",
			typeParameter: ty,
			upperBound: ty.extends,
			lowerBound: null,
		};
	}
	if ("typeArgs" in tree && tree.typeArgs) {
		return (
			addTypeArgs(scope, instantiateType(ty, new Map()), tree.typeArgs, ctx) ??
			anyType()
		);
	}
	return instantiateNonGenericType(ty);
}

function _resolveNestedReferenceTypeTree(
	scope: ScopeChainNode,
	lhsResolved: ReferenceType | PackageInfo | AnyType,
	tree: ReferenceTypeTree | NameTree,
	ctx: AnalysisCtx
) {
	if (lhsResolved.type === "any") return lhsResolved;

	const ident =
		tree instanceof ReferenceTypeTree ? tree.identifier : tree.identifier;
	const rhs = ident.identifier;

	let rhsResolved: PossiblyAmbiguous<
		NonGenericType | ParameterizedType | RawType
	> | null = null;

	if (lhsResolved.type === "package") {
		const clazz = lhsResolved.topLevelClasses.get(rhs) ?? null;
		if (clazz) {
			rhsResolved = instantiateNonGenericType(clazz);
		} else {
			const pkg = lhsResolved.subpackages.get(rhs) ?? null;
			if (pkg) {
				return (tree.referent = ident.referent = pkg);
			} else {
				ctx.reportError(
					new CompileError(`Cannot find type ${rhs}`, ident.span)
				);
				return anyType();
			}
		}
	} else {
		rhsResolved = lookupMemberClass(lhsResolved, rhs, true);
	}
	if (!rhsResolved) {
		ctx.reportError(new CompileError(`Cannot find type ${rhs}`, ident.span));
		return anyType();
	} else if (rhsResolved.type === "ambiguous") {
		ctx.reportError(
			new CompileError(`Reference to type ${rhs} is ambiguous`, ident.span)
		);
		return anyType();
	}

	const accessError = accessibilityError(unparameterize(rhsResolved), scope);
	if (accessError) {
		ctx.reportError(
			new CompileError(`Member is inaccessible: ${accessError}`, ident.span)
		);
		return anyType();
	}

	if (tree instanceof ReferenceTypeTree && tree.typeArgs) {
		rhsResolved = addTypeArgs(scope, rhsResolved, tree.typeArgs, ctx);
	} else {
		if (rhsResolved.type === "parameterized") {
			rhsResolved = {
				type: "raw",
				rawOf: unparameterize(rhsResolved),
			};
		}
	}

	tree.referent = ident.referent = rhsResolved
		? unparameterize(rhsResolved)
		: null;
	return rhsResolved ?? anyType();
}

function _resolveReferenceTypeTreeImpl(
	scope: ScopeChainNode,
	tree: ReferenceTypeTree | IdentTree | NameTree,
	ctx: AnalysisCtx
): ReferenceType | AnyType | PackageInfo {
	if (
		(tree instanceof ReferenceTypeTree && tree.lhs) ||
		tree instanceof NameTree
	) {
		const lhs = tree instanceof ReferenceTypeTree ? tree.lhs! : tree.qualifier;
		const lhsResolved = _resolveReferenceTypeTreeImpl(scope, lhs, ctx);
		if (!lhsResolved) return anyType(); // can't resolve LHS :(
		return _resolveNestedReferenceTypeTree(scope, lhsResolved, tree, ctx);
	} else {
		return _resolveTopLevelReferenceTypeTree(scope, tree, ctx);
	}
}

function resolveReferenceTypeTree(
	scope: ScopeChainNode,
	tree: ReferenceTypeTree | IdentTree | NameTree,
	ctx: AnalysisCtx
): ReferenceType | AnyType {
	const result = _resolveReferenceTypeTreeImpl(scope, tree, ctx);
	if (result?.type === "package") {
		ctx.reportError(
			new CompileError("Package name not allowed here", tree.span)
		);
		return anyType();
	}
	return result;
}

/**
 * Type parameter tree is something occurring in the type parameters of a class, function, or interface declaration,
 * e.g. class A <P extends ArrayList<P> & Cow> {}. In this case we resolve the right-hand bound and return it, if it
 * exists; if no bound exists, we return a success with a bound of null; and if the bound is malformed, we return null.
 */
export function resolveTypeParameterTree(
	scope: ScopeChainNode,
	tree: TypeParameterTree,
	ctx: AnalysisCtx
): {type: "success"; bound: ReferenceType | null} | null {
	const firstBound = tree.extendsClass;
	if (!firstBound) return {type: "success", bound: null};
	const boundsList: (ReferenceType | AnyType | null)[] = [
		// In example, ArrayList<P>
		resolveReferenceTypeTree(scope, firstBound, ctx),
	];
	const additionalBounds = tree.additionalBounds;
	if (additionalBounds) {
		// In example, Cow
		for (const bound of additionalBounds.types) {
			let additional: ReferenceType | AnyType | null = resolveReferenceTypeTree(
				scope,
				bound,
				ctx
			);
			// https://docs.oracle.com/javase/specs/jls/se8/html/jls-9.html#9.1.2
			if (maybeUnparameterize(additional)?.type !== "interface") {
				ctx.reportError(
					new CompileError(
						"Additional bound must be an interface type",
						bound.span
					)
				);
				additional = null;
			}
			boundsList.push(additional);
		}
	}
	const approvedBounds = boundsList.filter(
		(b): b is ReferenceType => b !== null && b.type !== "any"
	);
	if (approvedBounds.length !== boundsList.length)
		// At least one malformed bound :(
		return null;
	const bound: ReferenceType =
		approvedBounds.length === 1
			? approvedBounds[0]!
			: {type: "intersection", components: approvedBounds};
	return {
		type: "success",
		bound,
	};
}

/**
 * Check whether the type parameter, were it to be resolved to the given resolution, would induce a circularity
 * between type parameters (e.g. T extends T; or A extends B, B extends A). Note that it is okay for type parameters
 * to extend each other if indirected through a parameterized type, e.g. A extends C<B>, B extends A.
 *
 * CONTRACT: The function assumes that there are no existing circularities in the type parameter set.
 *
 * @param toCheck The type parameter to check.
 * @param resolution The type which we are about to resolve the type parameter to.
 */
export function typeParameterIsCircular(
	toCheck: TypeParameterInfo,
	resolution: ReferenceType | null
): string | undefined {
	let bailout = 0;
	let tyParam = resolution?.type === "typevar" && resolution?.typeParameter;
	const chain: TypeParameterInfo[] = [toCheck];
	while (tyParam) {
		chain.push(tyParam);
		if (tyParam === toCheck)
			return (
				"Type parameter circularity: " +
				chain.map((c) => prettyPrintInfo(c)).join(" → ")
			);
		if (tyParam?.extends?.type !== "typevar") break;
		tyParam = tyParam.extends.typeParameter;
		// Prevent infinite loops from crashing
		cafebabeAssert(
			bailout++ < 1000,
			"typeParameterIsCircular: Circularity between type parameters somehow already got in"
		);
	}
}

/**
 * Resolve the bounds of the type parameters, also checking for circularities.
 *
 * CONTRACT: Assumes that the referent of each TypeParameterTree has already been set. If not set, resolution will be
 * skipped for that type parameter.
 */
export function resolveTypeParameters(
	scope: ScopeChainNode,
	typeargs: TypeParametersTree | null,
	ctx: AnalysisCtx
) {
	if (!typeargs) return;
	typeargs.typeParameters.forEach((arg) => {
		// Resolve things like <A extends Cow>
		const argRef = arg.referent;
		if (!argRef) return; // condemned type parameter, skip resolution
		const resolution = resolveTypeParameterTree(scope, arg, ctx);

		if (resolution?.type === "success") {
			const circularError = typeParameterIsCircular(argRef, resolution.bound);
			if (circularError) {
				ctx.reportError(new CompileError(circularError, arg.span));
			} else {
				argRef.extends = resolution.bound;
			}
		}
	});
}

/**
 * Resolve the wildcard type of an expression like ? extends Object.
 *
 * Error behavior: If the bound is invalid or an any type is produced, an unbounded wildcard is produced.
 */
function resolveWildcardTree(
	scope: ScopeChainNode,
	tree: WildcardTree,
	ctx: AnalysisCtx
): WildcardType {
	let boundKind = tree.boundKind();
	let bound =
		boundKind === "none" || !tree.boundType
			? null
			: resolveTypeTree(scope, tree.boundType, ctx);
	if (bound === null || bound.type === "any") {
		// in case the resolution of the RHS failed.
		boundKind = "none";
		bound = null;
	}

	cafebabeAssert(
		bound?.type !== "null" &&
			bound?.type !== "primitive" &&
			bound?.type !== "wildcard",
		"Syntactically impossible wildcard bound"
	);

	return {
		type: "wildcard",
		boundKind,
		bound,
	};
}

/**
 * Resolve a type tree, which may be an array type, reference type, wildcard, or identifier/name tree (if the thing
 * initially appeared in an ambiguous context but which must resolve as a reference type upon further consideration).
 * @param scope
 * @param tree
 * @param ctx.reportError
 */
export function resolveTypeTree<
	T extends
		| ArrayTypeTree
		| ReferenceTypeTree
		| PrimitiveTypeTree
		| WildcardTree
		| IdentTree
		| NameTree,
>(
	scope: ScopeChainNode,
	tree: T,
	ctx: AnalysisCtx
): T extends WildcardTree ? GeneralTypeOrAny | WildcardType : GeneralTypeOrAny {
	if (tree instanceof PrimitiveTypeTree) {
		const ref =
			primitiveRefs[
				tokenTypeToString(
					tree.type
				) /* e.g. "int" */ as keyof typeof primitiveRefs
			];
		cafebabeAssert(ref !== undefined, "Primitive reference must exist");
		return ref;
	} else if (tree instanceof ArrayTypeTree) {
		return resolveArrayTypeTree(scope, tree, ctx);
	} else if (tree instanceof WildcardTree) {
		return resolveWildcardTree(scope, tree, ctx) as any;
	} else {
		return resolveReferenceTypeTree(scope, tree, ctx);
	}
}

/**
 * Get the root scope associated with the given scope (i.e., usually, the project root scope).
 */
export function getRootScope(
	scope: ScopeChainNode
): ScopeChainNode & {scopeType: "root"} {
	let bailout = 0;
	while (scope.prev) {
		scope = scope.prev;
		cafebabeAssert(bailout++ < 10000, "getRootScope: infinite loop");
	}
	return scope as ScopeChainNode & {scopeType: "root"};
}

/**
 * Get the current enclosing class and instantiate it, along with whether the context is static.
 */
export function getEnclosingClassContext(
	scope: ScopeChainNode
): {type: ReferenceType; static_: boolean} | null {
	return walkScopeChain(scope, (scopeNode, static_) => {
		const tree = scopeNode.tree;
		if (!tree?.isTypeDeclaration()) return null;
		cafebabeAssert(
			tree.referent !== null,
			"Tree referent should be resolved at this point"
		);

		return {type: instantiateType(tree.referent), static_};
	});
}

/**
 * Look up the type name in the current scope. Order of precedence is type parameters, followed by normal types,
 * followed by import on demand types. The first two have the same shadowing priority, while the latter is a
 * "resolution of last resort".
 *
 * In some cases, for example conflicting star imports, the class in question may be ambiguous, hence the
 * PossiblyAmbiguous<T> return value.
 *
 * @param scope The scope to resolve in.
 * @param ident The name to look up.
 */
function lookupType(
	scope: ScopeChainNode,
	ident: string
): PossiblyAmbiguous<
	ClassDeclarationInfo | InterfaceDeclarationInfo | TypeParameterInfo
> | null {
	const ty = walkScopeChain(scope, (s) => {
		return s.typeParameters?.get(ident) ?? s.types?.get(ident) ?? null;
	});
	if (ty) return ty; // unambiguous
	return walkScopeChain(scope, (s) => {
		return s.importOnDemandTypes?.get(ident) ?? null;
	});
}

/**
 * Add the given declaration to the list of imports on demand. Returns true if the declaration is new.
 * @param scope The scope to add to.
 * @param decl The declaration to add.
 */
export function addImportOnDemand(
	scope: ScopeChainNode,
	decl: ClassDeclarationInfo | InterfaceDeclarationInfo
) {
	scope.importOnDemandTypes ??= new Map();
	const map = scope.importOnDemandTypes,
		name = decl.simpleName;
	const existing = map.get(name);
	if (!existing) {
		map.set(name, decl);
	} else {
		switch (existing.type) {
			case "ambiguous": // ambiguity bonanza: ambiguities already exist, so add it if it's new
				if (!existing.resolutions.includes(decl))
					existing.resolutions.push(decl);
				return;
			case "interface":
			case "class":
				if (decl !== existing)
					// note that you're allowed to import the same type in multiple ways
					map.set(name, {type: "ambiguous", resolutions: [existing, decl]});
		}
	}
}

/**
 * Attempt to add the imported type to the scope, returning a non-empty error string if the addition fails because
 * a type with that name already exists.
 * @param scope Scope to add to.
 * @param decl Declaration to add.
 */
export function tryAddNamedImport(
	scope: ScopeChainNode,
	decl: ClassDeclarationInfo | InterfaceDeclarationInfo
): string | undefined {
	scope.types ??= new Map();
	const map = scope.types,
		name = decl.simpleName;
	const existing = map.get(name);
	if (
		existing &&
		existing !== decl /* permitted to import same class multiple times */
	) {
		return "Import of same name";
	}
	map.set(name, decl);
}

/**
 * See https://docs.oracle.com/javase/specs/jls/se8/html/jls-6.html#jls-6.5
 * PackageOrTypeName:
 *   Identifier
 *   PackageOrTypeName . Identifier
 * @param scope The scope in which resolution should be performed.
 * @param tree The tree to resolve.
 * @param currentPackage The package in which the name resides, for accessibility checks.
 * @param currentFile The file in which the name resides, for accessibility checks.
 * @param ctx.reportError Error reporting callback.
 * @param disallowPackage Whether to disallow this one being a package (used by resolveTypeName)
 */
export function resolvePackageOrTypeName(
	scope: ScopeChainNode,
	tree: NameTree | IdentTree,
	currentPackage: PackageInfo | null,
	currentFile: string,
	ctx: AnalysisCtx,
	disallowPackage: boolean
):
	| ClassDeclarationInfo
	| InterfaceDeclarationInfo
	| PackageInfo
	| TypeParameterInfo
	| null {
	if (tree instanceof NameTree) {
		const lhs = resolvePackageOrTypeName(
			scope,
			tree.qualifier,
			currentPackage,
			currentFile,
			ctx,
			false
		);
		if (!lhs) return null;
		const rhs = tree.identifier.identifier;
		if (!rhs) return null;

		let candidate:
			| PackageInfo
			| ClassDeclarationInfo
			| InterfaceDeclarationInfo
			| undefined;

		switch (lhs.type) {
			case "package": {
				candidate = lhs.topLevelClasses.get(rhs);
				if (!disallowPackage) candidate ??= lhs.subpackages.get(rhs);
				break;
			}
			case "class":
			case "interface": {
				candidate = lhs.memberClasses.get(rhs);
				break;
			}
		}
		if (!candidate)
			ctx.reportError(
				new CompileError(
					`${prettyPrintInfo(lhs, {capitalize: true})} does not have class <code>${rhs}</code>`,
					tree.identifier.span
				)
			);

		if (candidate && candidate.type !== "package") {
			const accessError = accessibilityError(candidate, scope);
			if (accessError) {
				ctx.reportError(
					new CompileError(
						`Member is inaccessible: ${accessError}`,
						tree.identifier.span
					)
				);
				candidate = undefined;
			}
		}

		return (tree.referent = tree.identifier.referent = candidate ?? null);
	} else {
		const pkg = disallowPackage
			? undefined
			: getRootScope(scope).packages!.get(tree.identifier);
		let resolvedTypeOrAmbiguous = lookupType(scope, tree.identifier);
		if (resolvedTypeOrAmbiguous) {
			if (resolvedTypeOrAmbiguous.type === "ambiguous") {
				ctx.reportError(
					new CompileError("Reference to type is ambiguous", tree.span)
				);
				resolvedTypeOrAmbiguous = null;
			} else {
				return (tree.referent = resolvedTypeOrAmbiguous);
			}
		} else if (pkg) {
			return (tree.referent = pkg);
		} else {
			ctx.reportError(new CompileError("Unknown package or type", tree.span));
		}
		return (tree.referent = resolvedTypeOrAmbiguous);
	}
}

export function resolveTypeName(
	scope: ScopeChainNode,
	tree: NameTree | IdentTree,
	currentPackage: PackageInfo | null,
	currentFile: string,
	ctx: AnalysisCtx
): ClassDeclarationInfo | InterfaceDeclarationInfo | null {
	return resolvePackageOrTypeName(
		scope,
		tree,
		currentPackage,
		currentFile,
		ctx,
		true /* disallow package */
	) as ClassDeclarationInfo | InterfaceDeclarationInfo;
}

const ANY_TYPE: AnyType = {type: "any"};
const NULL_TYPE: NullType = {type: "null"};
export function anyType(): AnyType {
	return ANY_TYPE;
}

export function nullType(): NullType {
	return NULL_TYPE;
}

/**
 * Resolve the type of an IntegerLiteralTree (will always be long or int).
 */
function _resolveIntegerLiteralType(
	_scope: ScopeChainNode,
	tree: IntegerLiteralTree,
	_ctx: AnalysisCtx
) {
	const ty = tree.isLong ? primitiveRefs.long : primitiveRefs.int;
	return (tree.javaType = ty);
}

/**
 * Resolve the type of a FloatLiteralTree (will always be float or double).
 */
function _resolveFloatLiteralType(
	_scope: ScopeChainNode,
	tree: FloatLiteralTree,
	_ctx: AnalysisCtx
) {
	const ty = tree.isF32() ? primitiveRefs.float : primitiveRefs.double;
	return (tree.javaType = ty);
}

function _resolveBooleanLiteralType(
	scope: ScopeChainNode,
	tree: BooleanLiteralTree,
	ctx: AnalysisCtx
) {
	tree.javaType = primitiveRefs.boolean;
	return tree.javaType;
}

function _resolveCharacterLiteralType(
	scope: ScopeChainNode,
	tree: CharacterLiteralTree,
	ctx: AnalysisCtx
) {
	tree.javaType = primitiveRefs.char;
	return tree.javaType;
}

/**
 * Resolve type arguments, i.e. type arguments as inputs to a generic class or method.
 */
function resolveTypeArgumentsTree(
	scope: ScopeChainNode,
	tree: TypeArgumentsTree | null,
	ctx: AnalysisCtx
) {
	if (!tree) return null;
	const args = tree.args.map((arg) => resolveTypeTree(scope, arg, ctx));
	for (const arg of args) {
		if (arg === null) return null;
		if (
			!(
				arg.type === "nongeneric" ||
				arg.type === "raw" ||
				arg.type === "parameterized"
			)
		) {
			ctx.reportError(
				new CompileError("Type arguments must be reference types", tree.span)
			);
			return null;
		}
	}
	return args as ReferenceType[];
}

function _resolveClassCreationType(
	scope: ScopeChainNode,
	lhs: GeneralTypeOrAny | null,
	tree: ClassCreationTree,
	ctx: AnalysisCtx
): GeneralTypeOrAny {
	if (!tree.type) return anyType();
	const referenceType = resolveTypeTree(
		scope,
		tree.type,
		ctx
	) as GeneralTypeOrAny;
	if (!(tree.type instanceof ReferenceTypeTree)) {
		ctx.reportError(new CompileError("Invalid class creation", tree.type.span));
		return anyType();
	}

	const tyArgs = resolveTypeArgumentsTree(scope, tree.typeArgs, ctx);
	const params =
		tree.args?.map((arg) => resolveExpressionType(scope, arg, ctx)) ?? null;

	void tyArgs;
	void params;

	return referenceType;
}

function _resolveThisTree(
	scope: ScopeChainNode,
	tree: ThisTree,
	ctx: AnalysisCtx
): GeneralTypeOrAny {
	const context = getEnclosingClassContext(scope);
	return context?.type ?? anyType();
}

function _resolveSuperTree(
	scope: ScopeChainNode,
	tree: SuperTree,
	ctx: AnalysisCtx
): GeneralTypeOrAny {
	const context = getEnclosingClassContext(scope);
	const super_ = context?.type
		? instantiateSuperclasses(scope, context.type, false)![0]
		: anyType();
	return super_ ?? anyType();
}

function resolveArrayCreationTree(
	scope: ScopeChainNode,
	tree: ArrayCreationTree,
	ctx: AnalysisCtx
) {
	let type = resolveTypeTree(scope, tree.type, ctx) as GeneralTypeOrAny;
	if (!tree.dims) return type;
	for (const dim of tree.dims) {
		const dimType = resolveExpressionType(scope, dim, ctx);
		if (
			dimType.type !== "any" &&
			unaryNumericPromote(dimType) !== primitiveRefs.int
		) {
			ctx.reportError(
				new CompileError(
					`Expected type 'int' for dimension, not '${prettyPrintType(dimType)}'`,
					dim.span
				)
			);
		}
		type = instantiateArray(type);
	}
	return type;
}

export enum ExceptionTraits {
	None = 0,
	Throwable = 1,
	Error = 2,
	Exception = 4,
	RuntimeException = 8,
}

/**
 * Get the exception traits of a type.
 */
export function getExceptionTraits(ty: GeneralTypeOrAny): ExceptionTraits {
	if (ty.type === "nongeneric") {
		const info = maybeUnparameterize(ty);
		if (info?.type !== "class") return ExceptionTraits.None;

		let classInfo: ClassDeclarationInfo | null = info;
		while (classInfo) {
			switch (classInfo.binaryName) {
				case "java.lang.Throwable":
					return ExceptionTraits.Throwable;
				case "java.lang.Error":
					return ExceptionTraits.Error | ExceptionTraits.Throwable;
				case "java.lang.Exception":
					return ExceptionTraits.Exception | ExceptionTraits.Throwable;
				case "java.lang.RuntimeException":
					return (
						ExceptionTraits.RuntimeException |
						ExceptionTraits.Exception |
						ExceptionTraits.Throwable
					);
			}
			if (!classInfo.extends) break; // reached Object
			classInfo = maybeUnparameterize(
				classInfo.extends
			) as ClassDeclarationInfo | null;
		}
	}

	return ExceptionTraits.None;
}

export function validateThrowsClause(
	scope: ScopeChainNode,
	throws: ThrowsTree | null,
	ctx: AnalysisCtx
): NonGenericType[] {
	if (!throws) return [];
	const classes: NonGenericType[] = [];
	for (const type of throws.types) {
		const resolution = resolveTypeTree(scope, type, ctx);
		if (!resolution || resolution.type === "any") continue;
		if (!(getExceptionTraits(resolution) & ExceptionTraits.Throwable)) {
			ctx.reportError(
				new CompileError(
					`Type '${prettyPrintType(resolution)}' is not throwable`,
					type.span
				)
			);
		} else if (resolution.type === "nongeneric") classes.push(resolution);
	}
	return classes;
}

function resolveThrowStatementTree(
	scope: ScopeChainNode,
	stmt: ThrowStatementTree,
	ctx: AnalysisCtx
) {
	const expression = stmt.expression;
	if (expression) {
		const ty = resolveExpressionType(scope, expression, ctx);
		const traits = getExceptionTraits(ty);
		// "It is a compile-time error if an ExceptionType mentioned in a throws clause is not a subtype (§4.10)
		// of Throwable."
		if (ty.type !== "any" && !(traits & ExceptionTraits.Throwable)) {
			ctx.reportError(
				new CompileError(
					`Type '${prettyPrintType(ty)}' is not throwable`,
					expression.span
				)
			);
		}
	}
}

function resolveTryStatementTree(
	scope: ScopeChainNode,
	tryStmt: TryStatementTree,
	ctx: AnalysisCtx
) {
	const tryScope = makeBlockScope(scope);
	if (tryStmt.resources) {
		for (const resources of tryStmt.resources) {
			if (resources instanceof LocalVariableDeclarationListTree) {
				for (const def of resolveLocalVariableDeclarationListTree(
					scope,
					resources,
					ctx,
					false
				)) {
					checkedAddToLocals(tryScope, def, ctx);
				}
			} else {
				// Resolve as a variable name
				resolveExpressionType(scope, resources, ctx);
			}
		}
	}
	tryStmt.block?.setScope(tryScope);
	resolveBlockTree(makeBlockScope(tryScope), tryStmt.block, ctx);

	for (const catchBlock of tryStmt.catches) {
		const catchScope = makeBlockScope(scope);
		const exceptionType = anyType();
		const param = catchBlock.exceptionName;
		if (param) {
			const def: LocalVariableDef = {
				type: "local",
				name: param.name.identifier,
				def: param.name,
				ty: exceptionType,
				parent: catchBlock,
				memberOf: null,
				variableArity: false,
				constantValue: null,
			};
			checkedAddToLocals(catchScope, def, ctx);
			param.name.referent = def;
		}
		resolveBlockTree(makeBlockScope(catchScope), catchBlock.block, ctx);
	}

	const finallyScope = makeBlockScope(scope);
	resolveBlockTree(finallyScope, tryStmt.finallyBlock?.block ?? null, ctx);
}

function resolveArrayAccessTree(
	scope: ScopeChainNode,
	tree: ArrayAccessTree,
	ctx: AnalysisCtx
) {
	const lhs = resolveExpressionType(scope, tree.lhs, ctx);
	const rhs = resolveExpressionType(scope, tree.index, ctx);
	if (lhs.type === "any") return lhs;
	if (lhs.type !== "array") {
		ctx.reportError(new CompileError("Not an array", tree.lhs.span));
		return anyType();
	}
	if (rhs.type !== "any" && unaryNumericPromote(rhs) !== primitiveRefs.int) {
		ctx.reportError(
			new CompileError(
				`Expected type 'int' for array index, not '${prettyPrintType(rhs)}'`,
				tree.index!.span
			)
		);
		return anyType();
	}
	return lhs.arrayOf;
}

function _resolveMemberAccessTree(
	scope: ScopeChainNode,
	lhs: GeneralType,
	right: IdentTree | null,
	ctx: AnalysisCtx
): GeneralTypeOrAny {
	if (!right) return anyType();

	const member = lookupMember(scope, lhs, right.identifier);
	if (!member) {
		ctx.reportError(new CompileError("Cannot find symbol", right.span));
		return anyType();
	} else if (member.type === "ambiguous") {
		ctx.reportError(
			new CompileError(
				`Reference to field '${right.identifier}' is ambiguous`,
				right.span
			)
		);
		return anyType();
	} else {
		const error = accessibilityError(member, scope, scope.isStatic);
		if (error) {
			ctx.reportError(new CompileError(error, right.span));
			return anyType();
		}
	}

	right.referent = member;
	return member.ty ?? anyType();
}

/**
 * Resolve a field access (but not a method invocation), e.g. cow().a .
 */
function _resolveFieldAccessTree(
	scope: ScopeChainNode,
	tree: FieldAccessTree,
	ctx: AnalysisCtx
) {
	const lhs = resolveExpressionType(scope, tree.lhs, ctx);
	if (lhs.type === "any") return lhs;

	const right = tree.rhs;
	let result: GeneralTypeOrAny;
	if (right instanceof ThisTree) {
		result = _resolveThisTree(scope, right, ctx);
	} else if (right instanceof SuperTree) {
		result = _resolveSuperTree(scope, right, ctx);
	} else if (right instanceof ClassCreationTree) {
		result = _resolveClassCreationType(scope, lhs, right, ctx);
	} else {
		result = _resolveMemberAccessTree(scope, lhs, right, ctx);
	}

	tree.javaType = result;
	return result;
}

/**
 * Test whether the argument of a method invocation is "potentially compatible" (see section 15.12.2.1) with a target
 * type.
 */
function _argumentIsPotentiallyCompatible(
	target: GeneralTypeOrAny,
	tree: BaseExpressionTree
) {
	if (target.type === "any") return true;

	if (tree instanceof LambdaExpressionTree) {
		// A lambda expression (§15.27) is potentially compatible with a functional interface type (§9.8) if all of the
		// following are true:
		// - The arity of the target type's function type is the same as the arity of the lambda expression.
		// - If the target type's function type has a void return, then the lambda body is either a statement
		// expression (§14.8) or a void-compatible block (§15.27.2).
		// - If the target type's function type has a (non-void) return type, then the lambda body is either an
		// expression or a value-compatible block (§15.27.2).
		return true; // TODO
	} else if (tree instanceof MethodReferenceTree) {
		// A method reference expression (§15.13) is potentially compatible with a functional interface type if, where
		// the type's function type arity is n, there exists at least one potentially applicable method for the method
		// reference expression with arity n (§15.13.1), and one of the following is true:
		// - The method reference expression has the form ReferenceType :: [TypeArguments] Identifier and at least one
		// potentially applicable method is i) static and supports arity n, or ii) not static and supports arity n-1.
		// - The method reference expression has some other form and at least one potentially applicable method is not
		// static.
		return true; // TODO
	} else if (tree instanceof ParenthesizedExpressionTree) {
		// A parenthesized expression (§15.8.5) is potentially compatible with a type if its contained expression is
		// potentially compatible with that type.
		return true;
	} else if (tree instanceof TernaryOperatorTree) {
		// A conditional expression (§15.25) is potentially compatible with a type if each of its second and third
		// operand expressions are potentially compatible with that type.
		return true;
	} else {
		// Everything else is "potentially compatible" with any type.
		return true;
	}
}

function _variableArityFunctionIsPotentiallyCompatible(
	fn: MethodDeclarationInfo,
	arguments_: BaseExpressionTree[]
) {
	if (fn.arguments.length - 1 > arguments_.length) return false;

	for (let i = 0; i < fn.arguments.length - 1; ++i) {
		const arg = fn.arguments[i];
		if (!arg) continue;
		if (!_argumentIsPotentiallyCompatible(arg, arguments_[i]!)) {
			return false;
		}
	}

	const last = fn.arguments.at(-1);
	cafebabeAssert(
		last !== undefined,
		"Variable arity without final argument is syntactically impossible"
	);

	if (fn.arguments.length === arguments_.length) {
		const finalArg = arguments_.at(-1)!;
		return (
			_argumentIsPotentiallyCompatible(last, finalArg) ||
			_argumentIsPotentiallyCompatible(instantiateArray(last), finalArg)
		);
	} else {
		for (let i = fn.arguments.length - 1; i < arguments_.length; ++i) {
			if (!_argumentIsPotentiallyCompatible(last, arguments_[i]!)) {
				return false;
			}
		}
	}

	return true;
}

function _fixedArityFunctionIsPotentiallyCompatible(
	fn: MethodDeclarationInfo,
	arguments_: BaseExpressionTree[]
) {
	if (fn.arguments.length !== arguments_.length) return false;

	for (let i = 0; i < fn.arguments.length; ++i) {
		const arg = fn.arguments[i]!;
		if (!arg) continue;
		if (!_argumentIsPotentiallyCompatible(arg, arguments_[i]!)) {
			return false;
		}
	}

	return true;
}

function removeDuplicateMethods(
	candidateMethods: TypeSubstitutionThunk<MethodDeclarationInfo>[]
) {
	return candidateMethods.filter((method, i) => {
		for (let j = 0; j < i - 1; ++j) {
			if (candidateMethods[j]!.val.name !== method.val.name) continue;
			if (
				candidateMethods[j]!.val.arguments.length !==
				method.val.arguments.length
			)
				continue;
			const lhs = candidateMethods[j]!.get();
			const cand = method.get();
			if (
				cand.arguments.every((arg, k) => typesAreEqual(arg, lhs.arguments[k]!))
			) {
				return false;
			}
		}
		return true;
	});
}

function staticPolicyCheck(f: {flags: number}, staticPolicy: StaticPolicy) {
	return (
		staticPolicy === StaticPolicy.ALL ||
		(staticPolicy === StaticPolicy.STATIC_ONLY) ===
			hasFlag(f, AccessorFlags.static)
	);
}

export function listMethods(
	scope: ScopeChainNode,
	staticPolicy: StaticPolicy,
	ty: ReferenceType
): TypeSubstitutionThunk<MethodDeclarationInfo>[] {
	const candidateMethods: TypeSubstitutionThunk<MethodDeclarationInfo>[] = [];
	walkInheritanceHierarchy(
		scope,
		ty,
		(super_) => {
			const inferredMap = maybeInferTPMap(super_);
			const unparameterized = maybeUnparameterize(super_);

			if (!unparameterized) return;
			const fns = [...unparameterized.functions.values()].flat();
			for (const fn of fns) {
				// Early elimination of functions which definitely won't work
				// 15.12.2.1: "The member [must be] accessible to the class or interface in which the method invocation
				// appears"
				if (accessibilityError(fn, scope)) continue;
				if (!staticPolicyCheck(fn, staticPolicy)) continue;

				candidateMethods.push(new TypeSubstitutionThunk(fn, inferredMap));
			}
		},
		true
	);
	return removeDuplicateMethods(candidateMethods);
}
export function listFields(
	scope: ScopeChainNode,
	staticPolicy: StaticPolicy,
	ty: ReferenceType
): TypeSubstitutionThunk<MemberFieldInfo>[] {
	if (ty.type === "array")
		return [new TypeSubstitutionThunk(arrayLengthField, new Map())];

	const candidates: TypeSubstitutionThunk<MemberFieldInfo>[] = [];
	walkInheritanceHierarchy(
		scope,
		ty,
		(super_) => {
			const inferredMap = maybeInferTPMap(super_);
			const unparameterized = maybeUnparameterize(super_);

			if (!unparameterized) return;

			const fields = [...unparameterized.members.values()].flat();
			if (fields.length === 0 || accessibilityError(fields[0]!, scope)) return;
			candidates.push(
				...fields
					.filter((f) => staticPolicyCheck(f, staticPolicy))
					.map((f) => new TypeSubstitutionThunk(f, inferredMap))
			);
		},
		true
	);
	return candidates;
}

export function instantiateTypeVariable(
	info: TypeParameterInfo
): TypeVariableType {
	return {
		type: "typevar",
		typeParameter: info,
		upperBound: info.extends,
		lowerBound: null,
	};
}

function erasure(
	scope: ScopeChainNode,
	ty: GeneralTypeOrAny
): GeneralTypeOrAny {
	switch (ty.type) {
		case "parameterized":
			return {type: "raw", rawOf: ty.parameterizes};
		case "array":
			return instantiateArray(erasure(scope, ty.arrayOf));
		case "typevar":
			// "The erasure of a type variable (§4.4) is the erasure of its leftmost bound."
			return ty.upperBound
				? erasure(scope, ty.upperBound)
				: primordialObject(scope);
		case "intersection":
			return erasure(scope, ty.components[0]!);
		default:
			return ty;
	}
}

function typesAreEqualUnderErasure(
	scope: ScopeChainNode,
	ty1: GeneralTypeOrAny,
	ty2: GeneralTypeOrAny
): boolean {
	switch (ty1.type) {
		case "array":
			if (ty2.type !== "array") return false;
			return typesAreEqualUnderErasure(scope, ty1.arrayOf, ty2.arrayOf);
		case "raw":
		case "parameterized":
		case "nongeneric":
			return unparameterize(ty1) === maybeUnparameterize(ty2);
		case "typevar":
			// "The erasure of a type variable (§4.4) is the erasure of its leftmost bound."
			return typesAreEqual(erasure(scope, ty1), erasure(scope, ty2));
		default:
			return typesAreEqual(ty1, ty2);
	}
}

/**
 * Check whether two methods have the same signature (crucially, ignoring the return type and name, and ignoring
 * parameterizations on functions). Used to error on duplicate function definitions.
 */
export function methodsAreOverrideEquivalent(
	scope: ScopeChainNode,
	method1: MethodDeclarationInfo,
	method2: MethodDeclarationInfo
): boolean {
	if (method1.arguments.length !== method2.arguments.length) return false;

	// Note that the # of type parameters is ignored
	return method1.arguments.every((arg1, i) =>
		typesAreEqualUnderErasure(scope, arg1, method2.arguments[i]!)
	);
}

export function validateProspectiveNewMethod(
	scope: ScopeChainNode,
	decl: ClassDeclarationInfo | InterfaceDeclarationInfo,
	method: MethodDeclarationInfo,
	ctx: AnalysisCtx
): boolean {
	return true; // TODO
}

function getCandidateMethods(
	scope: ScopeChainNode,
	isStatic: boolean,
	arguments_: BaseExpressionTree[],
	typeArguments: ReferenceType[],
	ty: ReferenceType,
	methodName: string
) {
	const candidateMethods: TypeSubstitutionThunk<MethodDeclarationInfo>[] = [];
	walkInheritanceHierarchy(
		scope,
		ty,
		(super_) => {
			const inferredMap = maybeInferTPMap(super_);
			const unparameterized = maybeUnparameterize(super_);

			if (!unparameterized) return;
			const fns = unparameterized.functions.get(methodName);
			if (!fns) return;

			for (const fn of fns) {
				// Early elimination of functions which definitely won't work
				// 15.12.2.1: "The member [must be] accessible to the class or interface in which the method invocation
				// appears"
				if (accessibilityError(fn, scope)) continue;
				// "If the member is a fixed arity method with arity n, the arity of the method invocation [must be]
				// equal to n..."
				if (!fn.isVariableArity && fn.arguments.length !== arguments_.length)
					continue;
				// "If the method invocation includes explicit type arguments, and the member is a generic method,
				// then the number of type arguments is equal to the number of type parameters of the method."
				const tparamCount = fn.typeParameters?.length ?? 0;
				if (
					typeArguments.length > 0 &&
					tparamCount > 0 &&
					tparamCount !== typeArguments.length
				)
					continue;

				if (fn.isVariableArity) {
					if (!_variableArityFunctionIsPotentiallyCompatible(fn, arguments_)) {
						continue;
					}
				} else {
					if (!_fixedArityFunctionIsPotentiallyCompatible(fn, arguments_)) {
						continue;
					}
				}

				if (isStatic && !hasFlag(fn, AccessorFlags.static)) continue;

				candidateMethods.push(new TypeSubstitutionThunk(fn, inferredMap));
			}
		},
		true
	);
	return candidateMethods;
}

// See section 15.12.2 for the grotesque algorithm
export function _searchForMethodAmongCandidates(
	scope: ScopeChainNode,
	isStatic: boolean,
	arguments_: BaseExpressionTree[],
	typeArguments: ReferenceType[],
	ty: ReferenceType,
	methodName: string,
	permitBoxingOrUnboxingConversion: boolean,
	permitVariableArity: boolean,
	candidateMethods: TypeSubstitutionThunk<MethodDeclarationInfo>[]
): PossiblyAmbiguous<TypeSubstitutionThunk<MethodDeclarationInfo>> | null {
	if (candidateMethods.length === 0) return null;

	// FOR NOW, just choose the first method and go with it
	// MAJOR TODO
	return candidateMethods[0]!;
}

function primordialObject(scope: ScopeChainNode): NonGenericType {
	return instantiateNonGenericType(
		lookupBinaryName(scope, "java.lang.Object")!
	) as NonGenericType;
}

function _typeToSearchForMethod(
	scope: ScopeChainNode,
	invokingOn: GeneralType
): ReferenceType | null {
	switch (invokingOn.type) {
		case "null":
		case "primitive":
			return null;
		case "typevar":
			// 15.12.1: "The class or interface to search is ... the upper bound of T if T is a type variable."
			return invokingOn.upperBound ?? primordialObject(scope);
	}
	return invokingOn;
}

function _resolveMethodInvocationTree(
	scope: ScopeChainNode,
	tree: MethodInvocationTree,
	ctx: AnalysisCtx
): GeneralTypeOrAny {
	let invokingOn = resolveExpressionType(scope, tree.expression, ctx);
	let static_ = false;
	if (!tree.expression) {
		// If the tree has no expression, e.g. "setX(0.0)", then we should search the enclosing
		// class for methods. Later, if this fails, we will also search static import declarations for the method
		// in question.
		const enclosing = getEnclosingClassContext(scope);
		if (!enclosing) return anyType();

		invokingOn = enclosing.type;
		static_ = enclosing.static_;
	}
	if (!invokingOn || invokingOn.type === "any") return anyType();

	const methodNameTree = tree.ident;
	if (!(methodNameTree instanceof IdentTree)) {
		// TODO
		return anyType();
	}

	const methodName = methodNameTree.identifier;
	const typeToSearch = _typeToSearchForMethod(scope, invokingOn);
	if (!typeToSearch) {
		ctx.reportError(
			new CompileError(
				`Cannot resolve method ${methodName} on primitive type`,
				tree.span
			)
		);
		return anyType();
	}

	const arguments_ = tree.args;
	const typeArguments =
		resolveTypeArgumentsTree(scope, tree.typeArguments, ctx) ?? [];

	const methodCandidates = getCandidateMethods(
		scope,
		static_,
		arguments_,
		typeArguments,
		typeToSearch,
		methodName
	);

	// "The first phase performs overload resolution without permitting boxing or unboxing conversion, or the
	// use of variable arity method invocation. ... The second phase performs overload resolution while allowing
	// boxing and unboxing, but still precludes the use of variable arity method invocation. ... The third phase
	// allows overloading to be combined with variable arity methods, boxing, and unboxing."
	const result =
		_searchForMethodAmongCandidates(
			scope,
			static_,
			arguments_,
			typeArguments,
			typeToSearch,
			methodName,
			false,
			false,
			methodCandidates
		) ??
		_searchForMethodAmongCandidates(
			scope,
			static_,
			arguments_,
			typeArguments,
			typeToSearch,
			methodName,
			true,
			false,
			methodCandidates
		) ??
		_searchForMethodAmongCandidates(
			scope,
			static_,
			arguments_,
			typeArguments,
			typeToSearch,
			methodName,
			true,
			true,
			methodCandidates
		);

	if (!result) {
		ctx.reportError(
			new CompileError(`Cannot find method ${methodName}`, methodNameTree.span)
		);
		return anyType();
	}

	if (result.type === "ambiguous") {
		ctx.reportError(
			new CompileError("Method invocation is ambiguous", methodNameTree.span)
		);
		return anyType();
	}

	arguments_.forEach((arg) => resolveExpressionType(scope, arg, ctx));

	methodNameTree.referent = result.get();
	return (tree.javaType = result.get().returns) ?? anyType();
}

function resolveTypeCastTree(
	scope: ScopeChainNode,
	tree: TypeCastTree,
	ctx: AnalysisCtx
) {
	// TODO deal with BoundsTree
	const ty = resolveTypeTree(scope, tree.type, ctx);
	const expr = resolveExpressionType(scope, tree.stmt, ctx);

	tree.javaType = ty;

	// TODO deal with invalid casts
	void expr;

	return ty;
}

export function resolveExpressionType(
	scope: ScopeChainNode,
	tree:
		| BaseExpressionTree
		| ArrayTypeTree
		| ReferenceTypeTree
		| PrimitiveTypeTree
		| null,
	ctx: AnalysisCtx,
	targetType: GeneralType | null = null
): GeneralTypeOrAny {
	if (!tree) return anyType();
	if (
		tree instanceof ArrayTypeTree ||
		tree instanceof ReferenceTypeTree ||
		tree instanceof PrimitiveTypeTree
	) {
		return resolveTypeTree(scope, tree, ctx) ?? anyType();
	}

	cafebabeAssert(
		tree instanceof BaseExpressionTree,
		"resolveExpressionType precondition"
	);
	if (tree.javaType) return tree.javaType; // already resolved

	let result: GeneralTypeOrAny = anyType();
	if (tree instanceof IdentTree || tree instanceof NameTree) {
		const resolved = resolveAmbiguousName(scope, tree, ctx);
		if (resolved) {
			if ("ty" in resolved && resolved.ty) result = resolved.ty;
			else if (resolved.type === "class" || resolved.type === "interface") {
				result = instantiateNonGenericType(resolved);
			}
		}
	} else if (tree instanceof ParenthesizedExpressionTree) {
		result = resolveExpressionType(scope, tree.contents, ctx);
	} else if (tree instanceof UnaryOperatorTree) {
		result = _resolveUnaryExpressionType(scope, tree, ctx);
	} else if (tree instanceof BinaryOperatorTree) {
		result = _resolveBinaryExpressionType(scope, tree, ctx);
	} else if (tree instanceof IntegerLiteralTree) {
		result = _resolveIntegerLiteralType(scope, tree, ctx);
	} else if (tree instanceof FloatLiteralTree) {
		result = _resolveFloatLiteralType(scope, tree, ctx);
	} else if (tree instanceof BooleanLiteralTree) {
		result = _resolveBooleanLiteralType(scope, tree, ctx);
	} else if (tree instanceof CharacterLiteralTree) {
		result = _resolveCharacterLiteralType(scope, tree, ctx);
	} else if (tree instanceof NullLiteralTree) {
		result = nullType();
	} else if (tree instanceof StringLiteralTree) {
		const java_lang_String = lookupBinaryName(scope, "java.lang.String");
		cafebabeAssert(java_lang_String !== null, "java.lang.String must exist");
		result = instantiateNonGenericType(java_lang_String);
	} else if (tree instanceof ClassCreationTree) {
		result = _resolveClassCreationType(scope, null, tree, ctx);
	} else if (tree instanceof FieldAccessTree) {
		result = _resolveFieldAccessTree(scope, tree, ctx);
	} else if (tree instanceof MethodInvocationTree) {
		result = _resolveMethodInvocationTree(scope, tree, ctx);
	} else if (tree instanceof ClassLiteralTree) {
		// e.g. int.class, Integer.class, java.lang.Integer.class
		//      ^^^        ^^^^^^^  type  ^^^^^^^^^^^^^^^^^
		const lhs = resolveTypeTree(
			scope,
			tree.type,
			ctx
		) as GeneralTypeOrAny | null;
		if (!lhs) return anyType();
		const boxed = maybeBox(scope, lhs) as ReferenceType;

		const java_lang_Class = lookupBinaryName(scope, "java.lang.Class");
		cafebabeAssert(java_lang_Class !== null, "java.lang.Class must exist");

		result = instantiateType(
			java_lang_Class,
			new Map([[java_lang_Class.typeParameters[0]!, boxed]])
		);
	} else if (tree instanceof TypeCastTree) {
		result = resolveTypeCastTree(scope, tree, ctx);
	} else if (tree instanceof ThisTree) {
		result = _resolveThisTree(scope, tree, ctx);
	} else if (tree instanceof SuperTree) {
		result = _resolveSuperTree(scope, tree, ctx);
	} else if (tree instanceof ArrayAccessTree) {
		result = resolveArrayAccessTree(scope, tree, ctx);
	} else if (tree instanceof ArrayCreationTree) {
		result = resolveArrayCreationTree(scope, tree, ctx);
	} else {
		for (const tr of tree.iterTokensAndTrees()) {
			if (tr instanceof BaseTree) {
				resolveBaseTree(scope, tr, ctx);
			}
		}
		result = anyType();
	}

	return (tree.javaType = result);
}

function resolveRootName(
	scope: ScopeChainNode,
	tree: IdentTree,
	ctx: AnalysisCtx
): PackageInfo | null {
	const candidate = getRootScope(scope)!.packages!.get(tree.identifier) ?? null;
	if (!candidate)
		ctx.reportError(
			new CompileError(`Cannot find name ${tree.identifier}`, tree.span)
		);
	return (tree.referent = candidate);
}

function _finishExpressionName(
	scope: ScopeChainNode,
	tree: NameTree | IdentTree,
	referent:
		| LocalVariableDef
		| MemberFieldInfo
		| PossiblyAmbiguous<MemberFieldInfo>,
	ctx: AnalysisCtx
) {
	if (referent.type === "ambiguous") {
		ctx.reportError(
			new CompileError(
				"Reference to field " + referent.resolutions[0]!.name + " is ambiguous",
				tree.span
			)
		);
		return (tree.referent = null);
	} else if (referent.type === "member") {
		const error = accessibilityError(referent, scope, scope.isStatic);
		if (error) {
			ctx.reportError(new CompileError(error, tree.span));
			return (tree.referent = null);
		}
	}
	tree.javaType = referent.ty;
	return (tree.referent = referent);
}

function _finishTypeName(
	tree: NameTree | IdentTree,
	referent:
		| InterfaceDeclarationInfo
		| ClassDeclarationInfo
		| PossiblyAmbiguous<InterfaceDeclarationInfo | ClassDeclarationInfo>,
	ctx: AnalysisCtx
) {
	if (referent.type === "ambiguous") {
		ctx.reportError(
			new CompileError(
				"Reference to type " +
					referent.resolutions[0]!.simpleName +
					" is ambiguous",
				tree.span
			)
		);
		return (tree.referent = null);
	}
	return (tree.referent = referent);
}

const arrayLengthField: MemberFieldInfo = {
	type: "member",
	file: "",
	builtin: true,
	flags: AccessorFlags.final | AccessorFlags.public,
	name: "length",
	ty: primitiveRefs.int,
	memberOf: "array",
};

/**
 * Resolve a so-called "AmbiguousName".
 */
export function resolveAmbiguousName(
	scope: ScopeChainNode,
	tree: NameTree | IdentTree,
	ctx: AnalysisCtx
):
	| ClassDeclarationInfo
	| InterfaceDeclarationInfo
	| PackageInfo
	| LocalVariableDef
	| MemberFieldInfo
	| null {
	// https://docs.oracle.com/javase/specs/jls/se8/html/jls-6.html#jls-6.5.2
	if (tree instanceof IdentTree) {
		const name = tree.identifier;

		// "If the Identifier appears within the scope (§6.3) of a local variable declaration (§14.4) or parameter
		// declaration (§8.4.1, §8.8.1, §14.20) or field declaration (§8.3) with that name, then the AmbiguousName
		// is reclassified as an ExpressionName.
		const exprName =
			walkScopeChain(scope, (node) => {
				return node.locals?.get(name) ?? node.memberFields?.get(name);
			}) ??
			// Otherwise, if a field of that name is declared in the compilation unit (§7.3) containing the Identifier by
			// a single-static-import declaration (§7.5.3), or by a static-import-on-demand declaration (§7.5.4)
			// then the AmbiguousName is reclassified as an ExpressionName.
			walkScopeChain(scope, (node) => {
				return node.staticallyImportedVariables?.get(name); // TODO
			});

		if (exprName) return _finishExpressionName(scope, tree, exprName, ctx);

		// Otherwise, if the Identifier appears within the scope (§6.3) of a top level class (§8 (Classes)) or
		// interface type declaration (§9 (Interfaces)), a local class declaration (§14.3) or member type declaration
		// (§8.5, §9.5) with that name, then the AmbiguousName is reclassified as a TypeName.
		const typeName =
			walkScopeChain(scope, (node) => {
				return node.types?.get(name);
			}) ??
			// Otherwise, if a type of that name is declared in the compilation unit (§7.3) containing the Identifier,
			// either by a single-type-import declaration (§7.5.1), or by a type-import-on-demand declaration (§7.5.2),
			// or by a single-static-import declaration (§7.5.3), or by a static-import-on-demand declaration (§7.5.4),
			// then the AmbiguousName is reclassified as a TypeName.
			walkScopeChain(scope, (node) => {
				return node.importOnDemandTypes?.get(name);
			});

		if (typeName) return _finishTypeName(tree, typeName, ctx);

		return resolveRootName(scope, tree, ctx);
	} else {
		// If the AmbiguousName is a qualified name, consisting of a name, a ".", and an Identifier, then the name to
		// the left of the "." is first reclassified, for it is itself an AmbiguousName.
		const left = resolveAmbiguousName(scope, tree.qualifier, ctx);
		const right = tree.identifier;
		tree.referent = right.referent = null;
		if (left === null)
			// Can't resolve LHS -> give up
			return null;

		if (left.type === "package") {
			// Look for class or subpackage
			// e.g. java.util  . ArrayList
			//      ^^^^^^^^^    ^^^^^^^^^
			//        left         right
			const ty =
				left.topLevelClasses.get(right.identifier) ??
				left.subpackages.get(right.identifier);
			if (ty) return (tree.referent = right.referent = ty);

			ctx.reportError(
				new CompileError(
					"Unknown package or class " + right.identifier,
					right.span
				)
			);
			return null;
		} else if (left.type === "interface" || left.type === "class") {
			// If the Identifier is the name of a method or field of the type denoted by TypeName, this AmbiguousName
			// is reclassified as an ExpressionName.

			const member = lookupMember(
				scope,
				instantiateType(left),
				right.identifier
			);
			if (member) {
				const finish = _finishExpressionName(scope, tree, member, ctx);
				if (finish) {
					right.javaType = tree.javaType;
					right.referent = tree.referent;
				}
				return finish;
			}

			// Otherwise, if the Identifier is the name of a member type of the type denoted by TypeName, this
			// AmbiguousName is reclassified as a TypeName.
			const memberType = left.memberClasses.get(right.identifier);
			if (memberType) return (tree.referent = right.referent = memberType);

			ctx.reportError(
				new CompileError(
					"Cannot resolve symbol " + right.identifier,
					right.span
				)
			);
			return null;
		} else {
			// ExpressionName
			const ty = resolveExpressionType(scope, tree.qualifier, ctx);
			const name = right.identifier;

			const rhs = lookupMember(scope, ty, name);
			if (rhs) return _finishExpressionName(scope, tree, rhs, ctx);
			ctx.reportError(
				new CompileError(
					"Cannot resolve symbol " + right.identifier,
					right.span
				)
			);
			return null;
		}
	}
}

export function resolveExpressionName(
	scope: ScopeChainNode,
	tree: NameTree | IdentTree,
	ctx: AnalysisCtx
): LocalVariableDef | MemberFieldInfo | null {
	resolveAmbiguousName(scope, tree, ctx);

	return null;
}

export function* enclosingClasses(
	info: ClassDeclarationInfo | InterfaceDeclarationInfo | null
): Generator<ClassDeclarationInfo | InterfaceDeclarationInfo> {
	if (!info) return;
	let parent: typeof info.enclosedBy = info;
	while ((parent = parent.enclosedBy)) yield parent;
}

/**
 * Get the type parameters out of the declaration, without actually resolving their bounds.
 */
export function extractTypeParameters(
	parentDecl: TypeParameterInfo["parameterOf"],
	args: TypeParametersTree | null,
	ctx: AnalysisCtx
): TypeParameterInfo[] {
	if (args === null) return [];
	const result: TypeParameterInfo[] = [];
	const seen = new Map<string, TypeParameterInfo>();
	for (const arg of args.typeParameters) {
		const name = arg.name.identifier;
		if (seen.has(name)) {
			ctx.reportError(
				new CompileError(`Duplicate type parameter ${name}`, arg.name.span)
			);
		} else {
			const info: TypeParameterInfo | null = (arg.referent = {
				type: "tparam",
				name,
				parameterOf: parentDecl,
				extends: null,
			});
			arg.name.referent = info;
			seen.set(name, info);
			result.push(info);
		}
	}

	return result;
}

/**
 * Walk the classes in the given scope or package structure.
 */
export function walkPackageClasses(
	rootScope: PackageInfo,
	options: WalkOptions,
	callback: (info: ClassDeclarationInfo | InterfaceDeclarationInfo) => boolean
) {
	const useCallback = options?.excludeNested
		? callback
		: (info: ClassDeclarationInfo | InterfaceDeclarationInfo) => {
				if (!callback(info)) return false;
				const queue: (ClassDeclarationInfo | InterfaceDeclarationInfo)[] = [
					...info.memberClasses.values(),
				];
				while (queue.length) {
					const clazz = queue.shift()!;
					if (!callback(clazz)) return false;
					queue.push(...clazz.memberClasses.values());
				}
				return true;
			};

	walkPackages(rootScope, options, (pkg) =>
		[...pkg.topLevelClasses.values()].every(useCallback)
	);
}

/**
 * Walk all packages accessible from the root scope.
 */
export function walkAllPackages(
	rootScope: ScopeChainNode,
	options: WalkOptions,
	callback: (info: PackageInfo) => boolean
) {
	cafebabeAssert(
		rootScope.scopeType === "root",
		"walkAllPackages precondition"
	);
	for (const pkg of rootScope.packages?.values() ?? []) {
		if (pkg.builtin && options.excludeBuiltin) continue;
		walkPackages(pkg, options, callback);
	}
}

/**
 * Walk all classes accessible from the root scope.
 */
export function walkAllDefinedClasses(
	rootScope: ScopeChainNode,
	options: WalkOptions,
	callback: (info: ClassDeclarationInfo | InterfaceDeclarationInfo) => boolean
) {
	cafebabeAssert(
		rootScope.scopeType === "root",
		"walkScopeClasses precondition"
	);

	function visitRootScopeTypes(
		type: ClassDeclarationInfo | InterfaceDeclarationInfo
	) {
		callback(type);
		for (const member of type.memberClasses.values())
			visitRootScopeTypes(member);
	}

	rootScope.types?.forEach(visitRootScopeTypes);
	for (const pkg of rootScope.packages?.values() ?? []) {
		if (pkg.builtin && options.excludeBuiltin) continue;
		walkPackageClasses(pkg, options, callback);
	}
}

/**
 * Compute the binary name of a class or interface.
 *
 * See https://docs.oracle.com/javase/specs/jls/se8/html/jls-13.html.
 *
 * @param parent Parent package or class/interface, or null if this is a top-level type.
 * @param name The name of the interface;
 */
export function computeBinaryName(
	parent: ClassDeclarationInfo | InterfaceDeclarationInfo | PackageInfo | null,
	name: string
) {
	cafebabeAssert(isValidJavaIdentifier(name), "computeBinaryName");
	if (parent === null) return name;
	switch (parent.type) {
		case "package":
			// "The binary name of a top level type (§7.6) is its canonical name (§6.7)."
			return parent.fullyQualifiedName + "." + name;
		case "class":
		case "interface":
			// "The binary name of a member type (§8.5, §9.5) consists of the binary name of its immediately enclosing
			// type, followed by $, followed by the simple name of the member."
			return parent.binaryName + "$" + name;
	}
	cafebabeUnreachable("computeBinaryName");
}

export function listAccessibleTypes(
	scope: ScopeChainNode,
	ref: PackageInfo | ClassDeclarationInfo | InterfaceDeclarationInfo
): string[] {
	return [
		...(ref.type === "package"
			? ref.topLevelClasses.entries()
			: ref.memberClasses.entries()),
	]
		.filter(([x, v]) => {
			return accessibilityError(v, scope) === undefined;
		})
		.map(([x, v]) => x);
}

export function getOrCreatePackage(
	rootScope: ScopeChainNode,
	tree: NameTree | IdentTree | null
): PackageInfo | null {
	if (!tree) return null;
	cafebabeAssert(
		rootScope.scopeType === "root",
		"getOrCreatePackage precondition"
	);

	const trees: IdentTree[] = [];
	while (tree) {
		if (tree instanceof IdentTree) {
			trees.push(tree);
			break;
		}
		trees.push(tree.identifier);
		tree = tree.qualifier;
	}

	trees.reverse();
	let currentPackage = (trees[0]!.referent = getOrCreateRootLevelPackage(
		rootScope,
		trees[0]!.identifier
	));
	for (let i = 1; i < trees.length; i++) {
		const ident = trees[i]!.identifier;
		currentPackage = trees[i]!.referent = getOrInsert(
			currentPackage.subpackages,
			ident,
			() =>
				({
					type: "package",
					name: ident,
					fullyQualifiedName: `${currentPackage.fullyQualifiedName}.${ident}`,
					topLevelClasses: new Map(),
					parent: currentPackage,
					subpackages: new Map(),
					description: "",
					builtin: false,
					scope: null,
				}) as PackageInfo
		);
	}
	return currentPackage;
}

function getOrInsert<T>(map: Map<string, T>, key: string, insert: () => T): T {
	let val = map.get(key);
	if (!val) {
		val = insert();
		map.set(key, val);
	}
	return val;
}

export function getOrCreateRootLevelPackage(
	scope: ScopeChainNode,
	name: string
): PackageInfo {
	cafebabeAssert(isValidJavaIdentifier(name), "Invalid package name");
	cafebabeAssert(
		scope.prev === null,
		"Packages should only be created in the root scope"
	);
	return getOrInsert(
		(scope.packages ??= new Map()),
		name,
		(): PackageInfo => ({
			type: "package",
			name,
			fullyQualifiedName: name,
			topLevelClasses: new Map(),
			parent: null,
			subpackages: new Map(),
			description: "",
			builtin: false,
			scope: null,
		})
	);
}

export function instantiateArray(
	resolvedType: PrimitiveType | ReferenceType | AnyType | NullType
): ArrayType | AnyType {
	return resolvedType.type === "any" || resolvedType.type === "null"
		? anyType()
		: {type: "array", arrayOf: resolvedType};
}

function resolveLocalVariableDeclarationTree(
	scope: ScopeChainNode,
	decl: LocalVariableDeclarationTree,
	parent: LocalVariableDeclarationListTree,
	resolvedType: GeneralTypeOrAny,
	ctx: AnalysisCtx,
	computeConstantValue: boolean
): LocalVariableDef | null {
	const ident = decl.name?.identifier;
	if (!ident) return null;

	// Process C-style brackets
	if (resolvedType.type !== "any" && resolvedType.type !== "null") {
		const additionalDims = decl.cStyleBrackets?.bracketPairs.length ?? 0;
		for (let i = 0; i < additionalDims; ++i)
			resolvedType = instantiateArray(resolvedType);
	}

	const declContents = decl.rhs;
	if (declContents) resolveExpressionType(scope, declContents, ctx);

	const MAX_DETAIL = 60;

	const def: LocalVariableDef = {
		type: "local",
		name: ident,
		def: decl,
		ty: resolvedType,
		parent,
		memberOf: null,
		variableArity: false,
		constantValue: computeConstantValue
			? (declContents?.prettyPrint()?.slice(0, MAX_DETAIL) ?? null)
			: null,
	};

	decl.name.referent = def;
	return def;
}

export function resolveLocalVariableDeclarationListTree(
	scope: ScopeChainNode,
	elem: LocalVariableDeclarationListTree,
	ctx: AnalysisCtx,
	computeConstantValue: boolean
): LocalVariableDef[] {
	const resolvedType = elem.ty ? resolveTypeTree(scope, elem.ty, ctx) : null;
	if (!resolvedType) return [];

	const ok: LocalVariableDef[] = [];
	for (const decl of elem.decls) {
		const resolved = resolveLocalVariableDeclarationTree(
			scope,
			decl,
			elem,
			resolvedType,
			ctx,
			computeConstantValue
		);
		if (resolved) {
			ok.push(resolved);
		}
	}
	return ok;
}

function checkedAddToLocals(
	scope: ScopeChainNode,
	def: LocalVariableDef,
	ctx: AnalysisCtx
) {
	if (definitionOfVariableAlreadyInScope(scope, def)) {
		ctx.reportError(
			new CompileError("Duplicate variable name", def.def.name!.span)
		);
	} else {
		scope.locals ??= new Map();
		scope.locals!.set(def.name, def);
	}
}

function isEnum(switchingOn: NonGenericType) {
	const extends_ = switchingOn.ty.extends;
	return (
		!Array.isArray(extends_) &&
		extends_?.type === "parameterized" &&
		extends_.parameterizes.binaryName === "java.lang.Enum"
	);
}

export function resolveSwitchStatementTree(
	scope: ScopeChainNode,
	tree: SwitchStatementTree,
	ctx: AnalysisCtx
) {
	const switchingOn = resolveExpressionType(scope, tree.switchExpr, ctx);
	const switchScope = makeBlockScope(scope);

	if (switchingOn.type === "nongeneric" && isEnum(switchingOn)) {
		// Add all enum constants to the scope of the switch statement
		switchScope.memberFields = new Map(switchingOn.ty.members);
	}

	if (tree.components) {
		tree.components.forEach((comp) => {
			comp.setScope(switchScope);
			resolveBaseTree(switchScope, comp, ctx);
		});
	}
}

function resolveLabel(
	stmt: IdentTree | null,
	scope: ScopeChainNode,
	ctx: AnalysisCtx
): LabelInfo | null {
	const label = stmt?.identifier;
	if (!label) return null;

	const labelInfo = listLabelsInScope(scope).get(label);
	if (!labelInfo) {
		ctx.reportError(new CompileError(`Cannot find label ${label}`, stmt.span));
		return null;
	}
	return labelInfo;
}

export function listLabelsInScope(
	scope: ScopeChainNode | null
): Map<string, LabelInfo> {
	const labels = new Map<string, LabelInfo>();
	walkScopeChain(scope, (node) => {
		if (node.labels) {
			for (const [name, info] of node.labels) {
				labels.set(name, info);
			}
		}
		if (node.scopeType !== "block") return false; // done searching for labels
	});
	return labels;
}

export function resolveLabelledStatementTree(
	scope: ScopeChainNode,
	tree: LabelledStatementTree,
	ctx: AnalysisCtx
) {
	const label = tree.label;
	// It is illegal to nest labels like cow: { cow: {} }
	const existingInOuter = listLabelsInScope(scope);
	if (existingInOuter.has(label.identifier)) {
		ctx.reportError(
			new CompileError(`Duplicate label ${label.identifier}`, label.span)
		);
	}

	function isLoop(loopTree: BaseTree | null): boolean {
		return (
			loopTree instanceof ForLoopTree ||
			loopTree instanceof WhileLoopTree ||
			loopTree instanceof DoWhileLoopTree
		);
	}

	const labelledStatementScope = makeBlockScope(scope);
	const labelInfo: LabelInfo = {
		type: "label",
		label: label.identifier,
		scope: labelledStatementScope,
		precedesLoop: isLoop(tree.statement),
	};
	labelledStatementScope.labels = new Map([[label.identifier, labelInfo]]);
	label.referent = labelInfo;
	tree.scope = labelledStatementScope;
	resolveBaseTree(labelledStatementScope, tree.statement, ctx);
}

function resolveBreakOrContinueStatement(
	scope: ScopeChainNode,
	stmt: BreakStatementTree | ContinueStatementTree,
	ctx: AnalysisCtx
) {
	const lab = resolveLabel(stmt.label, scope, ctx);
	if (lab && stmt instanceof ContinueStatementTree && !lab.precedesLoop) {
		ctx.reportError(
			new CompileError("Continue statement to label not in loop", stmt.span)
		);
	}
}

function resolveExpressionStatementTree(
	scope: ScopeChainNode,
	stmt: ExpressionStatementTree,
	ctx: AnalysisCtx
) {
	const ty = resolveExpressionType(scope, stmt.expression, ctx);
	void ty;
}

/**
 * Check that the type is a valid type to appear in a condition (if/for/while) and report an error if not.
 * @param ty The type to check (should be boolean or Boolean)
 * @param span The span of the condition
 * @param ctx.reportError Error callback.
 */
function checkConditionType(
	ty: GeneralTypeOrAny,
	span: Span,
	ctx: AnalysisCtx
) {
	if (
		ty.type === "any" ||
		ty === primitiveRefs.boolean ||
		isClassOfBinaryName(ty, "java.lang.Boolean")
	) {
		return;
	}

	ctx.reportError(
		new CompileError(
			`Condition must be a boolean, not '${prettyPrintType(ty)}'`,
			span
		)
	);
}

function resolveControlFlowCondition(
	scope: ScopeChainNode,
	tree: BaseExpressionTree | null,
	ctx: AnalysisCtx
): GeneralTypeOrAny {
	if (!tree) return anyType();
	const ty = resolveExpressionType(scope, tree, ctx);
	checkConditionType(ty, tree.span, ctx);
	return ty;
}

/**
 * Resolve a while or do-while loop tree. The condition is checked for validity and a new scope is created for the
 * contents of the loop.
 */
function resolveWhileOrDoWhileLoopTree(
	scope: ScopeChainNode,
	stmt: WhileLoopTree | DoWhileLoopTree,
	ctx: AnalysisCtx
) {
	resolveControlFlowCondition(scope, stmt.condition, ctx);
	if (stmt.statement)
		resolveBaseTree(makeBlockScope(scope), stmt.statement, ctx);
}

function resolveForLoopTree(
	scope: ScopeChainNode,
	stmt: ForLoopTree,
	ctx: AnalysisCtx
) {
	const init = stmt.forInit;
	const forScope = makeBlockScope(scope);
	if (init instanceof LocalVariableDeclarationListTree) {
		const defs = resolveLocalVariableDeclarationListTree(
			scope,
			init,
			ctx,
			false
		);
		for (const newDef of defs) {
			checkedAddToLocals(forScope, newDef, ctx);
		}
	} else {
		resolveBaseTree(forScope, init, ctx);
	}

	stmt.statement?.setScope(forScope);
	stmt.forCond?.setScope(forScope);
	stmt.forUpdate?.setScope(forScope);

	resolveBaseTree(forScope, stmt.statement, ctx);
	resolveControlFlowCondition(forScope, stmt.forCond, ctx);
	resolveBaseTree(forScope, stmt.forUpdate, ctx);
}

function resolveEnhancedForLoopTree(
	scope: ScopeChainNode,
	stmt: EnhancedForLoopTree,
	ctx: AnalysisCtx
) {
	const forScope = makeBlockScope(scope);
	let ty: GeneralTypeOrAny | null = null;
	if (stmt.elemType) {
		ty = resolveTypeTree(scope, stmt.elemType, ctx) as GeneralTypeOrAny | null;
	}
	ty ??= anyType();

	const def: LocalVariableDef = {
		type: "local",
		name: stmt.varName.token.value,
		def: stmt.varName,
		ty,
		parent: stmt,
		memberOf: null,
		variableArity: false,
		constantValue: null,
	};

	checkedAddToLocals(forScope, def, ctx);
	stmt.varName.referent = def;

	stmt.statement?.setScope(forScope);
	resolveBaseTree(forScope, stmt.statement, ctx);
}

/**
 * Create a block scope with parent scope equal to the given scope.
 */
export function makeBlockScope(scope: ScopeChainNode): ScopeChainNode {
	return {
		scopeType: "block",
		locals: new Map(),
		prev: scope,
		isStatic: scope.isStatic,
	};
}

function definitionOfVariableAlreadyInScope(
	scope: ScopeChainNode,
	def: LocalVariableDef
): boolean {
	return !!walkScopeChain(scope, (scopeNode) => {
		if (scopeNode.locals?.has(def.name)) return true;
		if (scopeNode.scopeType === "function" || scopeNode.scopeType === "class")
			return false;
	});
}

function resolveIfStatementTree(
	scope: ScopeChainNode,
	stmt: IfStatementTree,
	ctx: AnalysisCtx
) {
	resolveControlFlowCondition(scope, stmt.condition, ctx);
	if (stmt.thenStatement)
		resolveBaseTree(makeBlockScope(scope), stmt.thenStatement, ctx);
	if (stmt.elseStatement)
		resolveBaseTree(makeBlockScope(scope), stmt.elseStatement, ctx);
}

function resolveBaseTree(
	scope: ScopeChainNode | null,
	stmt: BaseTree | null,
	ctx: AnalysisCtx
) {
	if (!stmt) return null;

	scope ??= stmt.scope;
	if (!scope) return;

	if (stmt instanceof LocalVariableDeclarationListTree) {
		const defs = resolveLocalVariableDeclarationListTree(
			scope,
			stmt,
			ctx,
			false
		);
		// Add defs to scope, reject duplicate definitions
		const locals: Map<string, LocalVariableDef> = (scope.locals ??= new Map());

		for (const newDef of defs) {
			if (definitionOfVariableAlreadyInScope(scope, newDef)) {
				ctx.reportError(
					new CompileError("Duplicate variable name", newDef.def.name!.span)
				);
			} else {
				locals.set(newDef.name, newDef);
			}
		}
	} else if (stmt instanceof ExpressionStatementTree) {
		resolveExpressionStatementTree(scope, stmt, ctx);
	} else if (stmt instanceof ForLoopTree) {
		resolveForLoopTree(scope, stmt, ctx);
	} else if (stmt instanceof EnhancedForLoopTree) {
		resolveEnhancedForLoopTree(scope, stmt, ctx);
	} else if (stmt instanceof IfStatementTree) {
		resolveIfStatementTree(scope, stmt, ctx);
	} else if (stmt instanceof WhileLoopTree || stmt instanceof DoWhileLoopTree) {
		resolveWhileOrDoWhileLoopTree(scope, stmt, ctx);
	} else if (stmt instanceof SwitchStatementTree) {
		resolveSwitchStatementTree(scope, stmt, ctx);
	} else if (stmt instanceof BlockTree) {
		resolveBlockTree(makeBlockScope(scope), stmt, ctx);
	} else if (stmt instanceof EmptyStatementTree) {
		ctx.reportError(
			new CompileError(
				"Empty statement. Is this semicolon intended?",
				stmt.span,
				"warning"
			)
		);
	} else if (stmt instanceof LabelledStatementTree) {
		resolveLabelledStatementTree(scope, stmt, ctx);
	} else if (
		stmt instanceof BreakStatementTree ||
		stmt instanceof ContinueStatementTree
	) {
		resolveBreakOrContinueStatement(scope, stmt, ctx);
	} else if (stmt instanceof BaseExpressionTree) {
		resolveExpressionType(scope, stmt, ctx);
	} else if (stmt instanceof TryStatementTree) {
		resolveTryStatementTree(scope, stmt, ctx);
	} else if (stmt instanceof ThrowStatementTree) {
		resolveThrowStatementTree(scope, stmt, ctx);
	} else {
		for (const tree of stmt.iterTokensAndTrees()) {
			if (tree instanceof BaseTree) {
				resolveBaseTree(scope, tree, ctx);
			}
		}
	}
}

/**
 * Given a block and the scope chain node associated with that block, resolve the types of everything in the block.
 * @param scope The scope of the block.
 * @param tree The tree to resolve types in.
 * @param ctx.reportError Error callback.
 */
export function resolveBlockTree(
	scope: ScopeChainNode,
	tree: BlockTree | null,
	ctx: AnalysisCtx
) {
	if (tree === null) return;
	tree.scope = scope;
	for (const stmt of tree.statements) {
		resolveBaseTree(scope, stmt, ctx);
	}
}

function instantiateSuperclasses(
	scope: ScopeChainNode,
	knownType: GeneralTypeOrAny | null,
	includeSuperinterfaces: boolean
): PermittedSuperclassType[] {
	if (!knownType) return [];
	const inferredMap = maybeInferTPMap(knownType);
	const ty = maybeUnparameterize(knownType);

	if (!ty) return [];

	switch (knownType.type) {
		case "null":
		case "primitive":
		case "any":
			break;
		case "typevar":
			return instantiateSuperclasses(
				scope,
				knownType.upperBound,
				includeSuperinterfaces
			);
		case "raw":
		case "nongeneric":
		case "parameterized": {
			const ev = ty!.extends;
			if (!ev) return []; // primordial Object
			const arr = Array.isArray(ev) ? [...ev] : [ev];
			if (includeSuperinterfaces && "implements" in ty) {
				const si = ty.implements;
				if (si) arr.push(...si);
			}
			return arr.map((elem) => substituteTypeParameters(elem, inferredMap));
		}
		case "array":
			const classes = [primordialObject(scope)];
			if (includeSuperinterfaces) {
				// https://docs.oracle.com/javase/specs/jls/se8/html/jls-4.html#jls-4.10.3
				// Cloneable and Serializable
				classes.push(
					instantiateNonGenericType(
						lookupBinaryName(scope, "java.lang.Cloneable")!
					) as NonGenericType
				);
				classes.push(
					instantiateNonGenericType(
						lookupBinaryName(scope, "java.io.Serializable")!
					) as NonGenericType
				);
			}
			return classes;
		case "intersection": {
			const list: PermittedSuperclassType[] = [];
			for (const component of knownType.components) {
				list.push(
					...instantiateSuperclasses(scope, component, includeSuperinterfaces)
				);
			}
			return list;
		}
	}

	return [];
}

export function walkInheritanceHierarchy(
	scope: ScopeChainNode,
	knownType: ReferenceType | null,
	callback: (ty: ReferenceType) => void | boolean,
	includeSuperinterfaces: boolean
) {
	if (!knownType) return;
	if (callback(knownType)) return;
	const supers = instantiateSuperclasses(
		scope,
		knownType,
		includeSuperinterfaces
	);
	if (!supers) return;
	for (const super_ of supers) {
		if (callback(super_)) {
			continue;
		}

		walkInheritanceHierarchy(scope, super_, callback, includeSuperinterfaces);
	}
}

/**
 * Thunk to delay computing parameterization substitutions for things like method declarations.
 */
export class TypeSubstitutionThunk<
	T extends MethodDeclarationInfo | MemberFieldInfo,
> {
	public readonly type = "thunk";
	cached: T | null = null;

	constructor(
		public readonly val: T,
		private readonly map: Map<
			TypeParameterInfo,
			ReferenceType | WildcardType
		> | null
	) {}

	private _get(): T {
		const map = this.map,
			val = this.val;
		if (!map) return val;

		switch (val.type) {
			case "member": {
				const result: MemberFieldInfo = {...val};
				result.ty = substituteTypeParameters(result.ty, map) as ReferenceType;
				return result as T;
			}
			case "function": {
				const fn: MethodDeclarationInfo = {...val};
				fn.arguments = fn.arguments.map(
					(arg) => substituteTypeParameters(arg, map) as GeneralType
				);
				fn.returns = substituteTypeParameters(fn.returns, map) as GeneralType;
				return fn as T;
			}
		}
	}

	get(): T {
		return (this.cached ??= this._get());
	}
}

export function listAccessibleRhs(
	scope: ScopeChainNode,
	knownType: GeneralTypeOrAny | null,
	static_: StaticPolicy
): (TypeSubstitutionThunk<MethodDeclarationInfo> | MemberFieldInfo)[] {
	if (!knownType) return [];
	switch (knownType.type) {
		case "null":
		case "primitive":
		case "any":
			break;
		case "parameterized":
		case "nongeneric":
		case "array":
		case "intersection":
		case "raw": {
			const members = listMembers(scope, static_, knownType);
			const fns = listMethods(scope, static_, knownType).filter(
				(fn) => !fn.val.isCtor
			);

			return [...fns, ...members.values()].flat();
		}
		case "typevar":
			return listAccessibleRhs(scope, knownType.upperBound, static_);
	}

	return [];
}

export function splitBinaryName(binaryName: string): {
	pkg: string[];
	clazz: string[];
} {
	const names = binaryName.split(".");

	return {
		pkg: names.slice(0, names.length - 1),
		clazz: names.at(-1)!.split("$"),
	};
}

/**
 * From the given scope, look up the given binary name (e.g. java.util.Map$Entry). (Anonymous classes are not yet
 * supported.)
 */
export function lookupBinaryName(
	scope: ScopeChainNode,
	binaryName: string
): ClassDeclarationInfo | InterfaceDeclarationInfo | null {
	scope = getRootScope(scope);
	const {pkg, clazz} = splitBinaryName(binaryName);
	let pkgMap = scope.packages!,
		next: PackageInfo | undefined;
	for (let i = 0; i < pkg.length; ++i) {
		next = pkgMap.get(pkg[i]!);
		if (!next) return null;
		pkgMap = next.subpackages;
	}
	let class1 = (
		next ? next.topLevelClasses.get(clazz[0]!) : scope.types!.get(clazz[0]!)
	)!;
	if (!class1) return null;
	for (let i = 1; i < clazz.length; ++i) {
		const nextClass = class1.memberClasses.get(clazz[i]!);
		if (!nextClass) return null;
		class1 = nextClass;
	}
	return class1;
}

export function accumulateDependenciesOfType(
	referent: TypeDeclarationInfo,
	reachedFrom: Map<TypeDeclarationInfo, TypeDeclarationInfo>
) {
	if (reachedFrom.has(referent)) return reachedFrom;

	const tree = referent.tree;
	if (!tree) return reachedFrom;

	function addAllTypesMentioned(ty: ReferenceTypeTree | null) {
		const info = ty?.referent;
		if (!info || info.type === "package" || info.type === "tparam") return;
		reachedFrom.set(info, referent);
		addAllTypesMentioned(ty?.lhs ?? null);
	}

	if (tree instanceof ClassDeclarationTree) {
		// A class C directly depends on a type T if T is mentioned in the extends or implements clause of C either as
		// a superclass or superinterface, or as a qualifier in the fully qualified form of a superclass or
		// superinterface name.
		addAllTypesMentioned(tree.extendsClause);
		tree.implementsClause?.forEach((impl) => addAllTypesMentioned(impl));
	} else if (tree instanceof InterfaceDeclarationTree) {
		// An interface I directly depends on a type T if T is mentioned in the extends clause of I either as a
		// superinterface or as a qualifier in the fully qualified form of a superinterface name.
		tree.extendsClause?.forEach((impl) => addAllTypesMentioned(impl));
	}

	return reachedFrom;
}

/**
 * Create a compile error for a circularity in class/interface definitions, attempting to resolve referent to
 * resolution and with the given dependency mapping.
 */
function createClassCircularityErrorString(
	referent: ClassDeclarationInfo | InterfaceDeclarationInfo,
	resolution: RawType | NonGenericType | ParameterizedType,
	deps: Map<TypeDeclarationInfo, TypeDeclarationInfo>
) {
	// Walk deps DAG backward from referent to unparameterize(resolution)
	let current = referent;
	const goal = unparameterize(resolution);
	const path: TypeDeclarationInfo[] = [current];
	while (current !== goal) {
		const next = deps.get(current);
		cafebabeAssert(
			next !== undefined,
			"createClassCircularityError precondition"
		);
		path.push(next);
		current = next;
	}
	path.reverse();
	return `Circular class/interface definition: ${path.map((c) => prettyPrintInfo(c)).join(" → ")}`;
}

export function classCircularityCheck(
	referent: ClassDeclarationInfo | InterfaceDeclarationInfo,
	resolution: RawType | NonGenericType | ParameterizedType
): string | undefined {
	// If the resolution depends on the referent, then we are SCREWED
	const deps = accumulateDependenciesOfType(
		unparameterize(resolution),
		new Map()
	);
	if (deps.has(referent)) {
		return createClassCircularityErrorString(referent, resolution, deps);
	}
}

/**
 * Check whether the type has any wildcards in its immediate type arguments.
 */
export function anyWildcardError(resolution: GeneralTypeOrAny) {
	if (resolution.type === "parameterized") {
		if (resolution.typeArguments.some((arg) => arg.type === "wildcard")) {
			return true;
		}
	}
	return false;
}

/**
 * Check whether two types are exactly equal. Especially useful for testing.
 */
export function typesAreEqual(
	ty1: GeneralTypeOrAny | WildcardType | null,
	ty2: GeneralTypeOrAny | WildcardType | null
): boolean {
	if (ty1 === null) return ty2 === null;
	if (ty2 === null) return false;

	if (ty1.type !== ty2.type) return false;
	if (ty1.type === "any" || ty2.type === "any") return true;

	switch (ty1.type) {
		case "null":
			return true;
		case "primitive":
			return ty1 === ty2; // because all primitives are references to the same objects
		case "raw":
			return ty1.rawOf === (ty2 as RawType).rawOf;
		case "nongeneric":
			return ty1.ty === (ty2 as NonGenericType).ty;
		case "wildcard":
			return (
				ty1.boundKind === (ty2 as WildcardType).boundKind &&
				typesAreEqual(ty1.bound, (ty2 as WildcardType).bound)
			);
		case "typevar":
		case "intersection":
			return false; // TODO
		case "parameterized": {
			const ty2_ = ty2 as ParameterizedType;
			return (
				ty1.parameterizes === ty2_.parameterizes &&
				ty1.typeArguments.every((arg1, i) =>
					typesAreEqual(arg1, ty2_.typeArguments[i]!)
				)
			);
		}
		case "array":
			return typesAreEqual(ty1.arrayOf, (ty2 as ArrayType).arrayOf);
	}
}

enum ConvFlags {
	none = 0,
	identity = 1,
	wideningPrimitive = 2,
	narrowingPrimitive = 4,
	wideningReference = 8,
	narrowingReference = 16,
	boxing = 32,
	unboxing = 64,
	unchecked = 128,
}

type CastInfo = {
	flags: ConvFlags;
};

// Order of columns in Table 5.5-A
const PRIMITIVE_GREAT_CHAIN_OF_BEING = [
	primitiveRefs.byte,
	primitiveRefs.short,
	primitiveRefs.char,
	primitiveRefs.int,
	primitiveRefs.long,
	primitiveRefs.float,
	primitiveRefs.double,
];

function castToPrimitiveExists(
	target: PrimitiveType,
	source: GeneralType
): CastInfo {
	// Transcription of Table 5.5-A. Casting conversions to primitive types
	if (typesAreEqual(target, source)) {
		// diagonal of upper half of table
		return {flags: ConvFlags.identity};
	}

	const sourceIsPrimordialObject = isClassOfBinaryName(
		source,
		"java.lang.Object"
	);
	const unboxed = tryUnbox(source);
	if (unboxed === source)
		// diagonal of lower half of table
		return {flags: ConvFlags.unboxing};
	if (target === primitiveRefs.boolean) {
		// boolean column of table
		const flags =
			unboxed === primitiveRefs.boolean
				? ConvFlags.unboxing
				: sourceIsPrimordialObject
					? ConvFlags.unboxing | ConvFlags.narrowingReference
					: ConvFlags.none;
		return {flags};
	}
	if (source === primitiveRefs.boolean)
		// boolean row of table
		return {flags: ConvFlags.none};
	if (source.type === "primitive") {
		// Upper half of the table
		if (target === primitiveRefs.char) {
			return {
				flags:
					source === primitiveRefs.byte
						? ConvFlags.wideningPrimitive | ConvFlags.narrowingPrimitive
						: ConvFlags.narrowingPrimitive,
			};
		}

		const targetI = PRIMITIVE_GREAT_CHAIN_OF_BEING.indexOf(target);
		const sourceI = PRIMITIVE_GREAT_CHAIN_OF_BEING.indexOf(source);
		return {
			flags:
				targetI > sourceI
					? ConvFlags.wideningPrimitive
					: ConvFlags.narrowingPrimitive,
		};
	} else if (unboxed) {
		// Lower half of the table (except row Object)
		if (target === primitiveRefs.char) return {flags: ConvFlags.none};
	}

	if (sourceIsPrimordialObject) {
		return {flags: ConvFlags.unboxing | ConvFlags.narrowingPrimitive};
	}
	return {flags: ConvFlags.none}; // TODO casting from superclasses to primitive
}

function castFromPrimitiveExists(target: GeneralType, source: PrimitiveType) {
	if (typesAreEqual(target, source)) {
		return {flags: ConvFlags.identity};
	}

	const unboxedTarget = tryUnbox(target);
	if (unboxedTarget === source) {
		return {flags: ConvFlags.boxing};
	}

	if (isClassOfBinaryName(target, "java.lang.Object")) {
		return {flags: ConvFlags.boxing | ConvFlags.wideningReference};
	}

	return {flags: ConvFlags.none};
}

/**
 * See https://docs.oracle.com/javase/specs/jls/se8/html/jls-5.html#jls-5.5 for the full rules.
 * Determines whether (target) source is a valid cast.
 *
 * @param target Type to convert to.
 * @param source Type to convert from.
 */
export function castingConversionExists(
	target: GeneralTypeOrAny,
	source: GeneralTypeOrAny
): CastInfo {
	if (target.type === "any" || source.type === "any")
		return {flags: ConvFlags.identity};

	if (target.type === "primitive") return castToPrimitiveExists(target, source);
	else if (source.type === "primitive")
		return castFromPrimitiveExists(target, source);

	// "Given a compile-time reference type S (source) and a compile-time reference type T (target), a casting
	// conversion exists from S to T if no compile-time errors occur due to the following rules."

	// "If S is a class type..."
	if (maybeUnparameterize(source)?.type === "class") {
		// "If T is a class type, then either |S| <: |T|, or |T| <: |S|. Otherwise, a compile-time error occurs."
		const targetClass = maybeUnparameterize(target);

		void targetClass;
	}

	return {flags: ConvFlags.identity}; // TODO
}

export function prettyPrintArgs(method: MethodDeclarationInfo): string {
	const args = method.arguments
		.map((p, i) => (p ? prettyPrintType(p) + " " : "") + method.names[i]!)
		.join(", ");

	return `(${args})`;
}

export function lookupConstructors(
	scope: ScopeChainNode,
	ty: GeneralTypeOrAny
): TypeSubstitutionThunk<MethodDeclarationInfo>[] | null {
	const unparam = maybeUnparameterize(ty);
	if (!unparam) return null;

	const tpMap = maybeInferTPMap(ty);
	const constructors = unparam.functions
		.get(unparam.simpleName)
		?.filter((fn) => !accessibilityError(fn, scope));

	return (
		constructors?.map((ctor) => new TypeSubstitutionThunk(ctor, tpMap)) ?? null
	);
}

/**
 * Three fields are apparently "final", but are actually set by the JVM. For example, System.out is declared in
 * System.java as  public static final PrintStream out = null;  In these cases, the constant designator shown in the
 * completions ("null") is confusing, so we prefer to remove it.
 *
 * See https://docs.oracle.com/javase/specs/jls/se7/html/jls-17.html#jls-17.5.4 for more info.
 *
 * @param decl The declaration we are attaching the field to.
 * @param fieldName The name of the field.
 */
export function putativelyFinalFieldActuallyChanges(
	decl: ClassDeclarationInfo | InterfaceDeclarationInfo,
	fieldName: string
) {
	if (decl.binaryName === "java.lang.System") {
		if (["in", "out", "err"].includes(fieldName)) return true;
	}

	return false;
}

export function registerClass(
	fileName: string,
	decl: EnumDeclarationTree | ClassDeclarationTree | InterfaceDeclarationTree,
	parent: TypeDeclarationInfo | PackageInfo | null,
	parentScope: ScopeChainNode,
	pkg: PackageInfo | null,
	insertInto: Map<string, TypeDeclarationInfo>,
	topLevel: boolean,
	saneDeclarations: (
		| EnumDeclarationTree
		| ClassDeclarationTree
		| InterfaceDeclarationTree
	)[],
	ctx: AnalysisCtx
): {topLevelInfo: ClassDeclarationInfo | InterfaceDeclarationInfo} | undefined {
	if (!decl.name) return;

	const simpleName = decl.name.identifier; // e.g. "ArrayList"

	// https://docs.oracle.com/javase/specs/jls/se8/html/jls-8.html#jls-8.1
	// "It is a compile-time error if a class has the same simple name as any of its enclosing classes
	// or interfaces."
	if (
		parent &&
		parent.type !== "package" &&
		[parent, ...enclosingClasses(parent)].some(
			(encl) => encl.simpleName === simpleName
		)
	) {
		ctx.reportError(
			new CompileError(
				"Class may not have the same name as its enclosing class",
				decl.name.span
			)
		);
		return;
	}

	const baseInfo: DeclarationInfoBase = {
		file: fileName,
		builtin: false,
		flags: modifiersToAccessorFlags(decl.modifiers), // TODO cure modifiers
	};

	if (
		!(decl instanceof ClassDeclarationTree) &&
		parentScope.scopeType === "class"
	) {
		// enum and interface declarations nested within a parent class are implicitly static
		baseInfo.flags |= AccessorFlags.static;
	}

	// https://docs.oracle.com/javase/specs/jls/se8/html/jls-8.html#jls-8.1.3
	// "An inner class is a nested class that is not explicitly or implicitly declared static. A member
	// class of an interface is implicitly static (§9.5) so is never considered to be an inner class."
	const isInner =
		!topLevel &&
		parent?.type !== "interface" &&
		!hasFlag(baseInfo, AccessorFlags.static);
	const typeParameters =
		decl instanceof EnumDeclarationTree
			? []
			: extractTypeParameters(
					baseInfo as any /* to induce circular reference */,
					decl.typeargs,
					ctx
				);

	// inner classes inherit extra type parameters from their parent
	if (isInner && parent && parent.type !== "package") {
		typeParameters.unshift(...parent.typeParameters);
	}

	const isDeclEnum = decl instanceof EnumDeclarationTree;
	const mixinInfo: InterfaceOrClassMixin = {
		binaryName: computeBinaryName(parent, simpleName),
		typeParameters,
		simpleName,
		package: pkg,
		isTopLevel: topLevel,
		isInner,
		enclosedBy: parent?.type !== "package" ? parent : null,
		innerOf: parent?.type !== "package" && isInner ? parent : null,

		// TO BE FILLED LATER
		memberClasses: new Map(),
		functions: new Map(),
		members: new Map(),
		doc: decl.docComment?.value,
	};

	const info = Object.assign(baseInfo /* preserve reference */, mixinInfo);
	if (decl instanceof InterfaceDeclarationTree) {
		const interfaceInfo = info as InterfaceDeclarationInfo;
		interfaceInfo.type = "interface";
		interfaceInfo.extends = [];
	} else {
		const classInfo = info as ClassDeclarationInfo;
		classInfo.type = "class";
		classInfo.isEnum = isDeclEnum;
		classInfo.extends = null;

		// TO BE FILLED LATER
		classInfo.implements = [];
	}

	const filledInfo = info as ClassDeclarationInfo | InterfaceDeclarationInfo;
	const duplicateDeclError: CompileError | undefined = tryInsertType(
		insertInto,
		filledInfo,
		decl.span
	);
	if (duplicateDeclError) {
		ctx.reportError(duplicateDeclError);
		return;
	}

	parentScope.types!.set(simpleName, filledInfo);

	// Only continue analysis of the declaration if it's not a duplicate (within the package)
	saneDeclarations.push(decl);
	decl.referent = filledInfo;
	const declScope: ScopeChainNode = (decl.scope = {
		scopeType: "class",
		tree: decl,
		prev: parentScope,
		types: new Map(),
	});

	// Class is within scope of itself
	declScope.types!.set(simpleName, filledInfo);

	// Recursively register member classes
	decl.body?.forEach((tree) => {
		if (tree.isTypeDeclaration()) {
			registerClass(
				fileName,
				tree,
				filledInfo,
				declScope,
				pkg,
				info.memberClasses,
				false,
				saneDeclarations,
				ctx
			);
		}
	});

	return {topLevelInfo: filledInfo};
}
